import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import "../autorizacion/Autorizacion.css";
import customStyles from "./ReglasStyles.js";
import {
  actualizarReglaPrestacion,
  crearReglaPrestacion,
  deshabilitarRegla,
  eliminarRegla,
  extraerItemsReglas,
  obtenerAcciones,
  obtenerAlcances,
  obtenerAtributos,
  obtenerContextoPrioridadRegla,
  obtenerRelacionInversaRegla,
  obtenerReglasPorTipo,
  obtenerTodasLasReglas,
  obtenerTiposRegla,
  obtenerOperadores,
  obtenerCatalogoCondicionesMedicas,
  validarDuplicadoExactoRegla
} from "../../services/ReglasService";
import SelectAtributo from "./SelectAtributo.js";
import { hoyYYYYMMDD } from "../../utils/FechaUtils.js";
import {
  convertirADias,
  invertirCondicionNumerica,
  parsearDesdeDias,
  renderCondicionEdad,
  UNIDADES_EDAD,
} from "../../utils/edadUtils";
import { buscarItemsPorCodigo, buscarPracticas, buscarMedicamentos } from "../../services/NomencladorService";
import { listarPlanesCatalogo } from "../../services/PlanesService";
import { crearPracticaFueraPmo, reservarCodigoFueraPmo } from "../../services/NomencladorService";
import AsyncSelect from "react-select/async";
import { logSafeError, maskString } from "../../utils/safeLogging";
import {
  construirCondicionesNoRequiereDesdeElegibilidad,
  esReglaNoRequiereAutorizacion,
  formatearReglaElegibilidad,
} from "./noRequiereUtils";
import { resolverRelacionInversaLocal } from "./relacionInversaUtils";

const practicasOptions = [];

const ambitoOptions = [{ value: "Ambulatorio", label: "Ambulatorio" }];
const SIN_PLAN_VALUE = "__SIN_PLAN__";
const PLAN_FUERA_PMO = "FUERA-PMO";
const SIN_PLAN_OPTION = { value: SIN_PLAN_VALUE, label: "Sin plan asignado" };

// Normaliza decisiones backend (DERIVA -> DERIVAR)
const normalizeDecision = (decision) => {
  const d = String(decision ?? "").trim().toUpperCase();
  if (d === "NO_REQUIERE_AUTORIZACION") return "NO_REQUIERE_AUTORIZACION";
  if (d === "NO_REQUIERE_INFO") return "NO_REQUIERE_INFORMACION";
  if (d === "NO_REQUIERE_INFORMACION_ADICIONAL") return "NO_REQUIERE_INFORMACION";
  if (d === "NO_AUTORIZAR" || d === "NO_AUTORIZADA" || d === "NO_AUTORIZADO") return "NO_AUTORIZA";
  if (d === "TRAMITE_DUPLICADO") return "TRAMITE_DUPLICADO";
  if (d === "DERIVA" || d === "DERIVADO") return "DERIVAR";
  return d;
};

const REQUISITOS_JSON_PREFIX = "Requisitos==";
const ORDEN_MEDICA_REQUISITO = "Orden médica";

const joinRequisitos = (requisitos) =>
  (requisitos || [])
    .map((req) => {
      if (req == null) return "";
      if (typeof req === "string") return req.trim();
      return String(req?.descripcion ?? req?.nombre ?? req?.codigo ?? "").trim();
    })
    .filter(Boolean)
    .join(", ");

const normalizarRequisitos = (requisitos) => {
  if (Array.isArray(requisitos)) return joinRequisitos(requisitos);

  if (requisitos && typeof requisitos === "object") {
    if (Array.isArray(requisitos.item)) return joinRequisitos(requisitos.item);
    return joinRequisitos([requisitos]);
  }

  const normalized = String(requisitos ?? "").trim();
  if (!normalized) return "";

  const sanitized = normalized.startsWith(REQUISITOS_JSON_PREFIX)
    ? normalized.slice(REQUISITOS_JSON_PREFIX.length).trim()
    : normalized;

  if ((sanitized.startsWith("[") && sanitized.endsWith("]")) || (sanitized.startsWith("{") && sanitized.endsWith("}"))) {
    try {
      return normalizarRequisitos(JSON.parse(sanitized));
    } catch {
      return sanitized;
    }
  }

  return sanitized;
};

const requisitoFallback = (value) => {
  const normalized = normalizarRequisitos(value);
  return normalized || ORDEN_MEDICA_REQUISITO;
};

const isOrdenMedicaRequirement = (value) => toSig(requisitoFallback(value)) === toSig(ORDEN_MEDICA_REQUISITO);
const toSig = (value) =>
  String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
const dateSig = (value) => String(value ?? "").trim().slice(0, 10);
const RELACIONES_PAGE_SIZE = 200;
const CONDICION_MEDICA_ATTR = "condicionMedica";

const normalizarSeleccionPlanes = (selected) => {
  const items = Array.isArray(selected) ? selected : selected ? [selected] : [];
  const seen = new Set();
  return items.filter((item) => {
    const value = String(item?.value ?? "").trim();
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const planValueToPersist = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized === SIN_PLAN_VALUE) return null;
  return normalized;
};

const planesValuesToPersist = (planes) =>
  normalizarSeleccionPlanes(planes)
    .map((plan) => planValueToPersist(plan?.value))
    .filter((plan) => plan !== null);

const planesReglaToValues = (regla) => {
  const planes = Array.isArray(regla?.idPlanes)
    ? regla.idPlanes
    : Array.isArray(regla?.planes)
      ? regla.planes
      : null;

  if (planes) {
    return planes
      .map((plan) => String(plan?.idPlan ?? plan?.codigo ?? plan?.value ?? plan ?? "").trim().toUpperCase())
      .filter(Boolean);
  }

  const planRegla = String(regla?.plan ?? "").trim().toUpperCase();
  return planRegla ? planRegla.split(",").map((p) => p.trim()).filter(Boolean) : [];
};

const normalizeOptionalLong = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : normalized;
};

const buildPracticaOption = (item) => {
  const codigo = String(item?.codigoItemCobertura ?? item?.code ?? item?.codigo ?? item?.value ?? "").trim();
  const descripcion = String(item?.descripcion ?? item?.description ?? item?.label ?? "").trim();
  if (!codigo) return null;
  // La bandera la manda el back; el patron del codigo queda de respaldo por si la respuesta es vieja.
  const fueraDePmo = item?.fueraDePmo === true || /^99[0-9]{5}$/.test(codigo);
  return {
    value: codigo,
    label: descripcion ? `${codigo} - ${descripcion}` : codigo,
    descripcion,
    fueraDePmo,
  };
};

const buildMedicamentoOption = (item) => {
  const codigo = String(item?.codigo ?? "").trim();
  if (!codigo) return null;
  const descripcion = [item?.monodroga, item?.presentacion].filter(Boolean).join(" - ").trim();
  return {
    value: codigo,
    label: descripcion ? `${codigo} - ${descripcion}` : codigo,
    descripcion,
    fueraDePmo: false,
  };
};

const formatPracticaOption = (option) => (
  <span className="flex items-center gap-2">
    <span>{option?.label}</span>
    {option?.fueraDePmo && (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
        Fuera de PMO
      </span>
    )}
  </span>
);

const normalizarDescripcionPractica = (texto) => String(texto || "").trim().replace(/\s+/g, " ");

const decisionVisualConfig = (decisionRaw) => {
  const decision = normalizeDecision(decisionRaw);
  if (decision === "AUTORIZA") {
    return { key: "autoriza", icon: "✓", headline: "Autorización aprobada" };
  }
  if (decision === "NO_AUTORIZA") {
    return { key: "no_autoriza", icon: "✕", headline: "Solicitud rechazada" };
  }
  if (decision === "DERIVAR") {
    return { key: "derivar", icon: "↗", headline: "Caso derivado a revisión" };
  }
  if (decision === "NO_REQUIERE_AUTORIZACION") {
    return { key: "no_requiere_autorizacion", icon: "i", headline: "No requiere autorización" };
  }
  if (decision === "NO_REQUIERE_INFORMACION") {
    return { key: "no_requiere_informacion", icon: "i", headline: "No requiere información adicional" };
  }
  if (decision === "TRAMITE_DUPLICADO") {
    return { key: "duplicado", icon: "!", headline: "Trámite duplicado" };
  }
  if (decision === "OK" || decision === "EXITO" || decision === "ÉXITO") {
    return { key: "autoriza", icon: "✓", headline: "Operación completada" };
  }
  if (decision === "ERROR") {
    return { key: "error", icon: "!", headline: "No se pudo completar la operación" };
  }
  return { key: "neutro", icon: "•", headline: "Resultado de operación" };
};

const decisionToneConfig = (decisionKey, tipoMensaje) => {
  const tones = {
    autoriza: {
      accent: "#16a34a",
      accentDark: "#166534",
      surfaceStart: "#f3fdf7",
      surfaceEnd: "#ecfdf3",
      border: "#a7f3d0",
      soft: "#dcfce7",
    },
    no_autoriza: {
      accent: "#dc2626",
      accentDark: "#7f1d1d",
      surfaceStart: "#fff5f5",
      surfaceEnd: "#fef2f2",
      border: "#fecaca",
      soft: "#fee2e2",
    },
    derivar: {
      accent: "#d97706",
      accentDark: "#7c2d12",
      surfaceStart: "#fffaf2",
      surfaceEnd: "#fff7ed",
      border: "#fed7aa",
      soft: "#ffedd5",
    },
    no_requiere_autorizacion: {
      accent: "#0f766e",
      accentDark: "#115e59",
      surfaceStart: "#f0fdfa",
      surfaceEnd: "#ecfeff",
      border: "#99f6e4",
      soft: "#ccfbf1",
    },
    no_requiere_informacion: {
      accent: "#2563eb",
      accentDark: "#1e3a8a",
      surfaceStart: "#f3f7ff",
      surfaceEnd: "#eff6ff",
      border: "#bfdbfe",
      soft: "#dbeafe",
    },
    duplicado: {
      accent: "#b45309",
      accentDark: "#78350f",
      surfaceStart: "#fffaf0",
      surfaceEnd: "#fffbeb",
      border: "#fde68a",
      soft: "#fef3c7",
    },
    error: {
      accent: "#dc2626",
      accentDark: "#7f1d1d",
      surfaceStart: "#fff5f5",
      surfaceEnd: "#fef2f2",
      border: "#fecaca",
      soft: "#fee2e2",
    },
    neutro: {
      accent: "#475569",
      accentDark: "#334155",
      surfaceStart: "#f8fafc",
      surfaceEnd: "#f1f5f9",
      border: "#cbd5e1",
      soft: "#e2e8f0",
    },
  };

  if (tipoMensaje === "no-elegible" && decisionKey === "neutro") return tones.no_autoriza;
  if (tipoMensaje === "elegible" && decisionKey === "neutro") return tones.autoriza;

  return tones[decisionKey] || tones.neutro;
};

const getResultadoModalEstado = (mensaje) => {
  const decision = normalizeDecision(mensaje?.decision);
  if (decision === "ERROR" || mensaje?.tipo === "no-elegible") return "error";
  if (decision === "DERIVAR" || decision === "TRAMITE_DUPLICADO" || mensaje?.tipo === "neutro") return "warning";
  return "success";
};

const getResultadoModalTextos = ({ estado, modoEdicion }) => {
  if (estado === "success") {
    return {
      titulo: modoEdicion ? "Regla actualizada con éxito" : "Regla creada con éxito",
      subtitulo: modoEdicion ? "Los cambios fueron guardados correctamente." : "La regla fue registrada correctamente.",
    };
  }
  if (estado === "error") {
    return {
      titulo: modoEdicion ? "No se pudo actualizar la regla" : "No se pudo crear la regla",
      subtitulo: "Revisá los datos e intentá nuevamente.",
    };
  }
  return {
    titulo: "Operación completada con observaciones",
    subtitulo: "Revisá el detalle informado por el sistema.",
  };
};

function Reglas() {
  const location = useLocation();
  const navigate = useNavigate();
  const reglaParaEditar = location.state?.reglaParaEditar;

  const [nombreRegla, setNombreRegla] = useState("");
  const [idRegla, setIdRegla] = useState(null);
  const [ruleIdBase, setRuleIdBase] = useState("");
  const [resultadoEsperado, setResultadoEsperado] = useState(null);
  const [modalPracticaAbierto, setModalPracticaAbierto] = useState(false);
  const [practicaNuevaCodigo, setPracticaNuevaCodigo] = useState("");
  const [practicaNuevaDescripcion, setPracticaNuevaDescripcion] = useState("");
  const [practicaNuevaError, setPracticaNuevaError] = useState("");
  const [practicaNuevaGuardando, setPracticaNuevaGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const [vigenteDesde, setVigenteDesde] = useState(hoyYYYYMMDD());
  const [vigenteHasta, setVigenteHasta] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitPreparing, setSubmitPreparing] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [inhabilitarProgress, setInhabilitarProgress] = useState(0);
  const [mostrarCondicion, setMostrarCondicion] = useState(false);
  const [condiciones, setCondiciones] = useState([]);
  const [tipoCondicion, setTipoCondicion] = useState(null);
  const [operador, setOperador] = useState(null);
  const [valorCondicion, setValorCondicion] = useState("");
  const [valorNumericoEdad, setValorNumericoEdad] = useState("");
  const [unidadEdad, setUnidadEdad] = useState(UNIDADES_EDAD.DIAS);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [prioridad, setPrioridad] = useState(null);
  const [prioridadContextos, setPrioridadContextos] = useState([]);
  const [prioridadLoading, setPrioridadLoading] = useState(false);
  const [reglasExistentes, setReglasExistentes] = useState([]);
  const [reglasExistentesLoaded, setReglasExistentesLoaded] = useState(false);
  const [practicas] = useState(practicasOptions);
  const reglasExistentesPromiseRef = useRef(null);
  const inhabilitarAbortRef = useRef(null);

  const [descripcionRegla, setDescripcionRegla] = useState("");
  const [planesSeleccionados, setPlanesSeleccionados] = useState([]);
  const [idCobertura, setIdCobertura] = useState(null);
  const [idItemCobertura, setIdItemCobertura] = useState(null);
  const [idPack, setIdPack] = useState(null);
  const [tipoReglaData, setTipoReglaData] = useState({ id: null, codigo: '', desc: '' });
  const [alcanceData, setAlcanceData] = useState({ id: null, nombre: '', desc: '' });
  const [accionesCatalogo, setAccionesCatalogo] = useState([]);
  const [tiposReglaCatalogo, setTiposReglaCatalogo] = useState([]);
  const [alcancesCatalogo, setAlcancesCatalogo] = useState([]);
  const [operadoresCatalogo, setOperadoresCatalogo] = useState([]);
  const [planesCatalogo, setPlanesCatalogo] = useState([]);
  const [condicionesMedicasCatalogo, setCondicionesMedicasCatalogo] = useState([]);
  const [condicionesMedicasSeleccionadas, setCondicionesMedicasSeleccionadas] = useState([]);
  const [reglasElegibilidadCatalogo, setReglasElegibilidadCatalogo] = useState([]);
  const [reglasElegibilidadLoading, setReglasElegibilidadLoading] = useState(false);
  const [atributosCatalogo, setAtributosCatalogo] = useState([]);
  const [atributoNoRequiere, setAtributoNoRequiere] = useState(null);
  const [operadorNoRequiere, setOperadorNoRequiere] = useState(null);
  const [valorNoRequiere, setValorNoRequiere] = useState("");

  const [valorModificadoFinal, setValorModificadoFinal] = useState(null);
  const [cobertura, setCobertura] = useState({ id: null });
  const [item, setItem] = useState({ id: null });
  const [pack, setPack] = useState({ id: null });
  const [tipoSeleccionado, setTipoSeleccionado] = useState({ id: 0, codigo: '', nombre: '' });
  const [alcance, setAlcance] = useState({ id: 0, nombre: '', descripcion: '' });

  const tipoOptions = useMemo(() => tiposReglaCatalogo, [tiposReglaCatalogo]);
  const planOptions = useMemo(() => planesCatalogo, [planesCatalogo]);
  const planOptionsConSinPlan = useMemo(() => [SIN_PLAN_OPTION, ...planesCatalogo], [planesCatalogo]);
  const operadorOptions = useMemo(() => operadoresCatalogo, [operadoresCatalogo]);

  const esPrerequisitos = tipoCondicion?.value === "prerequisitos";
  const esCondicionMedica = tipoCondicion?.value === CONDICION_MEDICA_ATTR;
  const tieneCondicionPrerequisitos = condiciones.some((cond) => cond.tipo === "prerequisitos");
  const prerequisitoActivo = tieneCondicionPrerequisitos;
  const esElegibilidad = tipoSeleccionado?.codigo === "ELEGIBILIDAD";
  const esNoRequiereAutorizacion =
    modoEdicion &&
    esReglaNoRequiereAutorizacion(reglaParaEditar) &&
    String(reglaParaEditar?.ruleId || "").toUpperCase().includes("DINAMICA");

  useEffect(() => {
    if (modoEdicion || planesSeleccionados.length > 0) {
      return;
    }
  }, [modoEdicion, planesSeleccionados.length]);

  useEffect(() => {
    if (!modoEdicion || !reglaParaEditar) return;

    // Sincronizar Acción
    if (accionesCatalogo.length > 0 && !resultadoEsperado) {
      const decisionNormalizada = resolverDecisionRegla(reglaParaEditar);
      const accionEncontrada = resultadoOptions.find((opt) => opt.value === decisionNormalizada);
      if (accionEncontrada) setResultadoEsperado(accionEncontrada);
    }

    // Sincronizar Planes
    if (planesCatalogo.length > 0 || esElegibilidad) {
      const planesRegla = planesReglaToValues(reglaParaEditar);
      const planesEncontrados = planesRegla
        .map((p) => (p === SIN_PLAN_VALUE ? SIN_PLAN_OPTION : planOptionsConSinPlan.find((opt) => opt.value === p)))
        .filter(Boolean);
      
      const planesActualesInvalidos = planesSeleccionados.length === 0 || planesSeleccionados.some(p => !p || !p.value);

      if (planesEncontrados.length > 0 && planesActualesInvalidos) {
        setPlanesSeleccionados(planesEncontrados);
      } else if (planesRegla.length === 0 && planesSeleccionados.length > 0) {
        setPlanesSeleccionados([]);
      }
    }

    // Sincronizar Tipo de Regla
    if (tiposReglaCatalogo.length > 0 && (!tipoSeleccionado || !tipoSeleccionado.codigo)) {
       const tipoEncontrado = tipoOptions.find(opt => opt.value === "PRESTACION");
       if (tipoEncontrado) {
          setTipoSeleccionado({ id: 0, codigo: tipoEncontrado.value, nombre: tipoEncontrado.label });
          setTipoReglaData({ id: null, codigo: tipoEncontrado.value, desc: tipoEncontrado.label });
       }
    }
  }, [accionesCatalogo, planesCatalogo, tiposReglaCatalogo, modoEdicion, reglaParaEditar, esElegibilidad, planOptionsConSinPlan, planesSeleccionados]);

  const [valorModificadoCalculado, setValorModificadoCalculado] = useState(null);
  const [inverseError, setInverseError] = useState(null);
  const [confirmInversaOpen, setConfirmInversaOpen] = useState(false);
  const [accionInversaSeleccionada, setAccionInversaSeleccionada] = useState(null);
  const [confirmInhabilitarOpen, setConfirmInhabilitarOpen] = useState(false);
  const [inhabilitarError, setInhabilitarError] = useState("");
  const [inhabilitarResultado, setInhabilitarResultado] = useState(null);
  const [inhabilitarRelacion, setInhabilitarRelacion] = useState(null);
  const [pasoInhabilitar, setPasoInhabilitar] = useState("confirm");
  const [inhabilitarLoading, setInhabilitarLoading] = useState(false);
  const [modoInhabilitarRelacion, setModoInhabilitarRelacion] = useState("solo");
  const [confirmGuardarOpen, setConfirmGuardarOpen] = useState(false);
  const [confirmGuardarLoading, setConfirmGuardarLoading] = useState(false);
  const [guardarRelacion, setGuardarRelacion] = useState(null);
  const [actualizarConPareja, setActualizarConPareja] = useState(false);
  const [modoAccionPareja, setModoAccionPareja] = useState("mantener");
  const [accionParejaSeleccionada, setAccionParejaSeleccionada] = useState(null);
  const [accionParejaError, setAccionParejaError] = useState("");
  const [usarInversaPendiente, setUsarInversaPendiente] = useState(false);
  const [accionInversaPendiente, setAccionInversaPendiente] = useState("");
  const [ruleIdEdicion, setRuleIdEdicion] = useState(
    reglaParaEditar?.ruleId ? String(reglaParaEditar.ruleId) : ""
  );
  const [versionEdicion, setVersionEdicion] = useState(
    Number.isFinite(Number(reglaParaEditar?.version)) ? Number(reglaParaEditar.version) : null
  );
  const hayPlanElegido = (planesSeleccionados || []).some((p) => String(p?.value || "").trim() !== "");

  const hayPlanFueraPmo = useMemo(
    () =>
      (planesSeleccionados || []).some(
        (p) => String(p?.value || "").trim().toUpperCase() === PLAN_FUERA_PMO
      ),
    [planesSeleccionados]
  );

  const resultadoOptions = useMemo(() => {
    const opciones =
      (accionesCatalogo || []).length > 0
        ? accionesCatalogo
            .map((a) => {
              const value = normalizeDecision(a?.codigo);
              const label = String(a?.descripcion || a?.codigo || "").trim();
              if (!value || !label) return null;
              return { value, label };
            })
            .filter(Boolean)
        : [];
    // Una practica fuera del PMO no tiene cobertura definida, asi que nunca se resuelve
    // sola: siempre la tiene que mirar auditoria.
    return hayPlanFueraPmo ? opciones.filter((o) => o.value === "DERIVAR") : opciones;
  }, [accionesCatalogo, hayPlanFueraPmo]);

  useEffect(() => {
    if (!resultadoEsperado) return;
    const sigueDisponible = resultadoOptions.some((o) => o.value === resultadoEsperado.value);
    if (sigueDisponible) return;
    setResultadoEsperado(resultadoOptions.find((o) => o.value === "DERIVAR") || null);
  }, [resultadoOptions, resultadoEsperado]);

  const abrirModalPracticaNueva = async () => {
    setPracticaNuevaDescripcion("");
    setPracticaNuevaError("");
    setPracticaNuevaCodigo("");
    setModalPracticaAbierto(true);
    try {
      setPracticaNuevaCodigo(await reservarCodigoFueraPmo());
    } catch {
      setPracticaNuevaCodigo("");
    }
  };

  /*
    Al abrir el modal se reserva el codigo, asi el numero que ve el usuario ya es suyo. La practica
    recien se inserta en el OK: si cancela solo queda un hueco en la secuencia, sin filas huerfanas.
  */
  const confirmarPracticaNueva = async () => {
    const descripcion = normalizarDescripcionPractica(practicaNuevaDescripcion);
    if (descripcion.length < 3) {
      setPracticaNuevaError("Ingresá una descripción de al menos 3 caracteres.");
      return;
    }
    setPracticaNuevaGuardando(true);
    setPracticaNuevaError("");
    try {
      const creada = await crearPracticaFueraPmo(descripcion, practicaNuevaCodigo);
      const codigo = String(creada?.codigoItemCobertura || "");
      const opcion = {
        value: codigo,
        label: `${codigo} - ${creada?.descripcion || descripcion}`,
        descripcion: creada?.descripcion || descripcion,
      };
      setIdRegla(opcion);
      setRuleIdBase(codigo);
      setModalPracticaAbierto(false);
    } catch (error) {
      setPracticaNuevaError("No se pudo dar de alta la práctica. Reintentá en unos minutos.");
      logSafeError("warn", "Alta de practica fuera de PMO fallida", error);
    } finally {
      setPracticaNuevaGuardando(false);
    }
  };

  const fechaInicioInvalida = !vigenteDesde;
  const fechaFinInvalida = Boolean(vigenteHasta && vigenteDesde && vigenteHasta < vigenteDesde);
  const decisionVisual = decisionVisualConfig(mensaje?.decision);
  const decisionTone = decisionToneConfig(decisionVisual.key, mensaje?.tipo);
  const resultadoModalEstado = getResultadoModalEstado(mensaje);
  const resultadoModalTextos = getResultadoModalTextos({ estado: resultadoModalEstado, modoEdicion });
  const resultadoModalIcono = resultadoModalEstado === "success" ? "✓" : resultadoModalEstado === "error" ? "✕" : "!";
  const motivosResultado = Array.isArray(mensaje?.motivos)
    ? mensaje.motivos.filter((m) => String(m || "").trim() !== "")
    : [];
  const accionActualSeleccionada = normalizeDecision(resultadoEsperado?.value || "");
  const opcionesAccionPareja = useMemo(
    () =>
      (resultadoOptions || [])
        .filter(Boolean)
        .filter((opt) => normalizeDecision(opt?.value || "") !== accionActualSeleccionada),
    [resultadoOptions, accionActualSeleccionada]
  );
  const detalleResultado = resultadoModalEstado === "success" ? [] : motivosResultado.slice(0, 3);
  const detalleCortoResultado = detalleResultado[0] || "";
  const resultadoAcento = decisionTone.accent;
  const resultadoAcentoOscuro = decisionTone.accentDark;
  const cargarReglasExistentes = async ({ signal } = {}) => {
    const firstResponse = await obtenerTodasLasReglas({ page: 1, pageSize: RELACIONES_PAGE_SIZE, signal });
    const firstItems = extraerItemsReglas(firstResponse);

    if (!Array.isArray(firstItems) || firstItems.length === 0) {
      setReglasExistentes([]);
      setReglasExistentesLoaded(true);
      return [];
    }

    const totalReportado =
      Number(firstResponse?.total) ||
      Number(firstResponse?.totalItems) ||
      Number(firstResponse?.pagination?.total) ||
      Number(firstResponse?.meta?.total);

    if (!Number.isFinite(totalReportado) || totalReportado <= firstItems.length) {
      setReglasExistentes(firstItems);
      setReglasExistentesLoaded(true);
      return firstItems;
    }

    const totalPages = Math.ceil(totalReportado / RELACIONES_PAGE_SIZE);
    const otherPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
    const responses = await Promise.all(
      otherPages.map((page) => obtenerTodasLasReglas({ page, pageSize: RELACIONES_PAGE_SIZE, signal }))
    );

    const acumuladas = [...firstItems];
    for (const response of responses) {
      const items = extraerItemsReglas(response);
      if (Array.isArray(items)) {
        acumuladas.push(...items);
      }
    }

    setReglasExistentes(acumuladas);
    setReglasExistentesLoaded(true);
    return acumuladas;
  };

  const ensureReglasExistentes = async ({ force = false, signal } = {}) => {
    if (!force && reglasExistentesLoaded) {
      return reglasExistentes;
    }

    if (!force && reglasExistentesPromiseRef.current) {
      return reglasExistentesPromiseRef.current;
    }

    const request = cargarReglasExistentes({ signal }).finally(() => {
      reglasExistentesPromiseRef.current = null;
    });
    reglasExistentesPromiseRef.current = request;
    return request;
  };

  useEffect(() => {
    if (!mensaje) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [mensaje]);

  useEffect(() => {
    if (!esNoRequiereAutorizacion) return;
    let isActive = true;
    setReglasElegibilidadLoading(true);

    Promise.resolve(obtenerReglasPorTipo("ELEGIBILIDAD"))
      .then((response) => {
        if (!isActive) return;
        setReglasElegibilidadCatalogo(extraerItemsReglas(response));
      })
      .catch((error) => {
        if (!isActive) return;
        logSafeError("warn", "No se pudieron cargar reglas de elegibilidad para No Requiere", error);
        setReglasElegibilidadCatalogo([]);
      })
      .finally(() => {
        if (isActive) setReglasElegibilidadLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [esNoRequiereAutorizacion]);

  useEffect(() => {
    if (!loading || !confirmGuardarOpen) {
      setLoadingProgress(0);
      return;
    }
    setLoadingProgress(8);
    const timer = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 92) return prev;
        return Math.min(prev + Math.max(2, Math.round((100 - prev) / 10)), 92);
      });
    }, 700);
    return () => clearInterval(timer);
  }, [loading, confirmGuardarOpen]);

  useEffect(() => {
    if (!loading || !confirmInhabilitarOpen) {
      setInhabilitarProgress(0);
      return;
    }
    setInhabilitarProgress(8);
    const timer = setInterval(() => {
      setInhabilitarProgress((prev) => {
        if (prev >= 92) return prev;
        return Math.min(prev + Math.max(2, Math.round((100 - prev) / 10)), 92);
      });
    }, 700);
    return () => clearInterval(timer);
  }, [loading, confirmInhabilitarOpen]);

  useEffect(() => {
    if (!tipoSeleccionado?.codigo) {
      setTipoSeleccionado({ id: 0, codigo: "PRESTACION", nombre: "Prestación" });
      setTipoReglaData({ id: null, codigo: "PRESTACION", desc: "Prestación" });
    }
  }, [tipoSeleccionado?.codigo]);

  useEffect(() => {
    let isActive = true;

    (async () => {
      try {
        const results = await Promise.allSettled([
          obtenerAcciones(),
          obtenerTiposRegla(),
          obtenerAlcances(),
          obtenerAtributos(),
          obtenerOperadores(),
          listarPlanesCatalogo()
        ]);

        if (!isActive) return;
        const [acciones, tipos, alcances, atributos, operadores, planes] = results.map(
          (r) => (r.status === "fulfilled" ? r.value : [])
        );
        setAccionesCatalogo(acciones || []);
        setTiposReglaCatalogo(tipos || []);
        setAlcancesCatalogo(alcances || []);
        setAtributosCatalogo(atributos || []);
        setOperadoresCatalogo(operadores || []);
        setPlanesCatalogo(planes || []);

        const alcanceCobertura = (alcances || []).find(
          (alc) => String(alc?.nombre || '').toUpperCase() === "COBERTURA"
        );
        if (alcanceCobertura) {
          setAlcanceData({
            id: alcanceCobertura.idAlcance,
            nombre: alcanceCobertura.nombre,
            desc: alcanceCobertura.descripcion
          });
          setAlcance({
            id: alcanceCobertura.idAlcance,
            nombre: alcanceCobertura.nombre,
            descripcion: alcanceCobertura.descripcion
          });
        }
      } catch (e) {
        logSafeError("warn", "No se pudieron cargar catálogos de reglas_data", e);
      }
    })();

    (async () => {
      try {
        const condicionesMedicas = await obtenerCatalogoCondicionesMedicas();
        if (isActive) {
          setCondicionesMedicasCatalogo(condicionesMedicas || []);
        }
      } catch (e) {
        if (isActive) {
          setCondicionesMedicasCatalogo([]);
        }
        logSafeError("warn", "No se pudo cargar el catalogo de condiciones medicas", e);
      }
    })();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!tiposReglaCatalogo.length) return;
    const codigo = String(tipoSeleccionado?.codigo || "PRESTACION").toUpperCase();
    const tipo = tiposReglaCatalogo.find(
      (t) => String(t?.codigo || '').toUpperCase() === codigo
    );
    if (tipo) {
      setTipoReglaData({ id: tipo.idTipoRegla, codigo: tipo.codigo, desc: tipo.descripcion });
    }
  }, [tiposReglaCatalogo, tipoSeleccionado?.codigo]);

  useEffect(() => {
    if (!alcancesCatalogo.length || alcanceData?.nombre) return;
    const alcanceCobertura = alcancesCatalogo.find(
      (alc) => String(alc?.nombre || '').toUpperCase() === "COBERTURA"
    );
    if (alcanceCobertura) {
      setAlcanceData({
        id: alcanceCobertura.idAlcance,
        nombre: alcanceCobertura.nombre,
        desc: alcanceCobertura.descripcion
      });
      setAlcance({
        id: alcanceCobertura.idAlcance,
        nombre: alcanceCobertura.nombre,
        descripcion: alcanceCobertura.descripcion
      });
    }
  }, [alcancesCatalogo, alcanceData?.nombre]);

  const obtenerTipoDato = (atributo) => {
    const key = String(atributo || "").trim().toLowerCase();
    switch (key) {
      case "edad":
      case "topeMensual":
      case "topeanual":
      case "nivelmorosidad":
      case "cantidad":
        return "ENTERO";
      case "activo":
        return "BOOLEANO";
      case "condicionmedica":
        return "JSON";
      default:
        return "TEXTO";
    }
  };

  const normalizarValorPorTipoDato = (valor, tipoDato) => {
    if (tipoDato === "ENTERO") {
      const n = Number(valor);
      return Number.isFinite(n) ? n : valor;
    }
    if (tipoDato === "BOOLEANO") {
      const v = String(valor ?? "").trim().toLowerCase();
      if (v === "true") return true;
      if (v === "false") return false;
      return valor;
    }
    if (tipoDato === "JSON") {
      return String(valor ?? "");
    }
    return String(valor ?? "");
  };

  const parseCondicionMedicaValor = (valor) => {
    if (Array.isArray(valor)) return valor;
    const raw = String(valor ?? "").trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return raw.split(",").map((item) => item.trim()).filter(Boolean);
    }
  };

  const obtenerAccionId = (codigoAccion) => {
    const accion = (accionesCatalogo || []).find(
      (a) => String(a?.codigo || '').toUpperCase() === String(codigoAccion || '').toUpperCase()
    );
    return accion?.idAccion ?? 0;
  };

  const resolverDecisionRegla = (regla) => {
    const candidatos = [
      regla?.accion?.decision,
      regla?.decision,
      regla?.accion?.codigo,
      regla?.accionCodigo,
      regla?.codigoAccion,
    ];

    const porTexto = candidatos
      .map((c) => normalizeDecision(c))
      .find((c) => String(c || "").trim() !== "");
    if (porTexto) return porTexto;

    const idAccion = Number(
      regla?.accion?.idAccion ??
      regla?.idAccion ??
      regla?.accionId ??
      NaN
    );
    if (Number.isFinite(idAccion)) {
      const accion = (accionesCatalogo || []).find((a) => Number(a?.idAccion) === idAccion);
      return normalizeDecision(accion?.codigo || "");
    }

    return "";
  };
  const accionParejaActual = resolverDecisionRegla(guardarRelacion?.parejaRule);

  const dedupeCondiciones = (condicionesRaw) => {
    const seen = new Set();
    const resultado = [];
    (condicionesRaw || []).forEach((cond) => {
      const rawTipo = String(cond?.tipo ?? cond?.atributo ?? "").trim();
      const operador = String(cond?.operador ?? "").trim();
      const valor = String(cond?.valor ?? "").trim();
      const key = `${rawTipo.toLowerCase()}__${operador}__${valor}`;
      if (!rawTipo || seen.has(key)) return;
      seen.add(key);
      resultado.push({
        tipo: rawTipo,
          operador: cond?.operador ?? operador,
          valor: cond?.valor ?? valor
      });
    });
    return resultado;
  };

  const condicionesNormalizadas = dedupeCondiciones(condiciones);
  const noRequiereElegibilidadKeys = ["activo", "cobertura", "moroso", "judicializado", "empadronado"];
  const noRequiereBaseEditableKeys = ["canal", "prestacioncodigo"];
  const noRequiereObligatoriasKeys = ["requiereautorizacion"];
  const noRequiereDinamicasObligatorias = [
    { tipo: "edad", operador: ">=", valor: "edadMinima", displayText: "edad >= edadMinima" },
    { tipo: "edad", operador: "<=", valor: "edadMaxima", displayText: "edad <= edadMaxima" },
    { tipo: "topeMensual", operador: "<", valor: "limiteMensual + 1", displayText: "topeMensual < limiteMensual + 1" },
    { tipo: "topeAnual", operador: "<", valor: "limiteAnual + 1", displayText: "topeAnual < limiteAnual + 1" },
    { tipo: "topeGeneral", operador: "<", valor: "limiteGeneral + 1", displayText: "topeGeneral < limiteGeneral + 1" },
  ];
  const condicionesNoRequiereSistema = condicionesNormalizadas.filter((cond) =>
    noRequiereElegibilidadKeys.includes(String(cond?.tipo || "").toLowerCase())
  );
  const condicionesNoRequiereObligatorias = condicionesNormalizadas.filter((cond) => {
    const tipo = String(cond?.tipo || "").toLowerCase();
    if (noRequiereObligatoriasKeys.includes(tipo)) return true;
    return !noRequiereElegibilidadKeys.includes(tipo) && !noRequiereBaseEditableKeys.includes(tipo);
  });
  const condicionesNoRequiereBase = condicionesNormalizadas.filter((cond) =>
    noRequiereBaseEditableKeys.includes(String(cond?.tipo || "").toLowerCase())
  );
  const condicionesNoRequiereObligatoriasVisibles = [
    ...condicionesNoRequiereObligatorias,
    ...noRequiereDinamicasObligatorias.filter((condicionDinamica) => {
      const tipoDinamico = String(condicionDinamica.tipo || "").toLowerCase();
      return !condicionesNoRequiereObligatorias.some(
        (cond) => String(cond?.tipo || "").toLowerCase() === tipoDinamico
      );
    }),
  ];
  const atributoBaseNoRequiereOptions = useMemo(() => {
    const formatAtributoCatalogoLabel = (attr) => {
      const nombre = String(attr?.nombre || "").trim();
      const descripcion = String(attr?.descripcion || "").trim();
      if (!nombre) return descripcion;
      if (!descripcion || descripcion === nombre) return nombre;
      return `${nombre} - ${descripcion}`;
    };
    const findAtributoCatalogo = (nombreBuscado) =>
      (atributosCatalogo || []).find(
        (attr) => String(attr?.nombre || "").trim().toLowerCase() === String(nombreBuscado || "").toLowerCase()
      );
    const canalCatalogo = findAtributoCatalogo("canal");
    const prestacionCatalogo = findAtributoCatalogo("prestacionCodigo") || findAtributoCatalogo("codigoPrestacion");
    return [
      {
        value: "canal",
        label: canalCatalogo ? formatAtributoCatalogoLabel(canalCatalogo) : "canal",
      },
      {
        value: "prestacionCodigo",
        label: prestacionCatalogo ? formatAtributoCatalogoLabel(prestacionCatalogo) : "codigoPrestacion",
      },
    ];
  }, [atributosCatalogo]);
  const operadorForzadoNoRequiere = useMemo(() => {
    const findOperadorNoRequiere = (candidatos) =>
      (operadorOptions || []).find((opt) => {
        const value = String(opt?.value || "").trim().toLowerCase();
        const label = String(opt?.label || "").trim().toLowerCase();
        return candidatos.some((candidato) => value === candidato || label === candidato);
      }) || null;
    const atributo = String(atributoNoRequiere?.value || "").toLowerCase();
    if (atributo === "canal") return findOperadorNoRequiere(["in", "incluido", "incluye"]);
    if (atributo === "prestacioncodigo") return findOperadorNoRequiere(["==", "=", "igual"]);
    return null;
  }, [atributoNoRequiere?.value, operadorOptions]);
  const operadorNoRequiereDisabled = Boolean(operadorForzadoNoRequiere);
  const esCondicionNoRequiereNoEditable = (cond) => {
    if (!esNoRequiereAutorizacion) return false;
    const tipo = String(cond?.tipo || "").toLowerCase();
    if (noRequiereObligatoriasKeys.includes(tipo)) return true;
    return !noRequiereElegibilidadKeys.includes(tipo) && !noRequiereBaseEditableKeys.includes(tipo);
  };
  useEffect(() => {
    if (!esNoRequiereAutorizacion || !operadorForzadoNoRequiere) return;
    if (String(operadorNoRequiere?.value || "") === String(operadorForzadoNoRequiere?.value || "")) return;
    setOperadorNoRequiere(operadorForzadoNoRequiere);
  }, [esNoRequiereAutorizacion, operadorForzadoNoRequiere, operadorNoRequiere?.value]);
  const condicionPrerequisitosInfo = condicionesNormalizadas.find(
    (cond) => String(cond?.tipo || "").toLowerCase() === "prerequisitos"
  );
  const mostrarInfoOrdenMedica = !esElegibilidad;
  const invertibleConditions = condicionesNormalizadas.filter(
    (cond) =>
      String(cond?.tipo || '').toLowerCase() !== "prestacioncodigo" &&
      String(cond?.tipo || '').toLowerCase() !== "prerequisitos" &&
      (cond?.operador === "<" || cond?.operador === ">")
  );
  const idReglaActualEdicion = String(ruleIdEdicion || reglaParaEditar?.ruleId || "").trim();
  const esReglaInversaActual = idReglaActualEdicion.endsWith("_INV");
  const reglaBaseActualEdicion = esReglaInversaActual
    ? idReglaActualEdicion.slice(0, -4)
    : idReglaActualEdicion;
  const parejaActualExisteEnEdicion =
    modoEdicion &&
    !esReglaInversaActual &&
    Array.isArray(reglasExistentes) &&
    (reglasExistentes || []).some((r) => {
      const rid = String(r?.idRegla || r?.ruleId || "").trim();
      if (!rid || rid === idReglaActualEdicion) return false;
      return rid === `${reglaBaseActualEdicion}_INV`;
    });
  const inversaDisponible =
    !esElegibilidad &&
    invertibleConditions.length > 0 &&
    (!modoEdicion || (!esReglaInversaActual && !parejaActualExisteEnEdicion));
  const opcionesAccionInversa = (resultadoOptions || []).filter(
    (opt) => String(opt?.value || "").trim() !== String(resultadoEsperado?.value || "").trim()
  );

  useEffect(() => {
    if (!inversaDisponible) {
      setInverseError(null);
    }
  }, [inversaDisponible]);

  useEffect(() => {
    if (!confirmInversaOpen) return;
    if (accionInversaSeleccionada?.value) return;
    setAccionInversaSeleccionada(opcionesAccionInversa[0] || null);
  }, [confirmInversaOpen, accionInversaSeleccionada?.value, opcionesAccionInversa]);

  useEffect(() => {
    if (!actualizarConPareja || modoAccionPareja !== "elegir") return;
    if (accionParejaSeleccionada?.value) return;
    const opciones = (opcionesAccionPareja || []).filter(Boolean);
    setAccionParejaSeleccionada(opciones[0] || null);
  }, [actualizarConPareja, modoAccionPareja, accionParejaSeleccionada?.value, opcionesAccionPareja]);

  useEffect(() => {
    if (modoAccionPareja !== "elegir") {
      setAccionParejaError("");
      return;
    }
    if (accionParejaSeleccionada?.value) return;
  }, [modoAccionPareja, accionParejaSeleccionada?.value]);

  const handleChangePrioridad = (e) => {
    const soloNumeros = e.target.value.replace(/[^0-9]/g, '');
    setPrioridad(soloNumeros);
  };

  const basePrestacionFromRuleId = (rid) => String(rid).split("_v")[0];

  const parseVersionFromRuleId = (rid) => {
    const m = String(rid).match(/_v(\d+)(?:000)?$/);
    return m ? Number(m[1]) : null;
  };

  const nextVersionForPrestacion = (prestacionId, reglasFuente = reglasExistentes) => {
    const base = String(prestacionId);
    const versions = (reglasFuente || [])
        .filter((r) => basePrestacionFromRuleId(r.idRegla || r.ruleId) === base)
        .map((r) => parseVersionFromRuleId(r.idRegla || r.ruleId) ?? r.version ?? 1)
        .filter((v) => Number.isFinite(v));

    const max = versions.length ? Math.max(...versions) : 0;
    return max + 1;
  };

  const normalizarCondicionesParaFirma = (conds) =>
    (conds || [])
      .map((cond) => {
        const atributo = toSig(cond?.tipo ?? cond?.atributo ?? "");
        const operador = String(cond?.operador ?? "").trim();
        const valorRaw = cond?.valor;
        const valor = typeof valorRaw === "number" ? String(valorRaw) : toSig(valorRaw);
        return `${atributo}|${operador}|${valor}`;
      })
      .filter(Boolean)
      .sort()
      .join("&&");

  const firmaReglaActual = (() => {
    const tipo = toSig(tipoSeleccionado?.codigo || "PRESTACION");
    const nombre = toSig(nombreRegla);
    const decision = toSig(normalizeDecision(resultadoEsperado?.value));
    const prioridadSig = String(Number(prioridad) || "");
    const desde = dateSig(vigenteDesde);
    const hasta = dateSig(vigenteHasta);
    const codigoPrestacionSig = esElegibilidad
      ? toSig(ruleIdBase || nombreRegla)
      : toSig(idRegla?.value);
    const planSig = (planesSeleccionados || [])
      .map((p) => planValueToPersist(p?.value) ?? SIN_PLAN_VALUE)
      .map((p) => toSig(p))
      .sort()
      .join(",");
    const condicionesSig = normalizarCondicionesParaFirma(condicionesNormalizadas);
    return [tipo, nombre, decision, prioridadSig, desde, hasta, codigoPrestacionSig, planSig, condicionesSig].join("||");
  })();

  const construirFirmaReglaExistente = (regla) => {
    const tipo = toSig(regla?.tipoRegla || regla?.tipoRegla?.codigo || "PRESTACION");
    const nombre = toSig(regla?.nombre);
    const decision = toSig(resolverDecisionRegla(regla));
    const prioridadSig = String(Number(regla?.prioridad) || "");
    const desde = dateSig(regla?.vigenteDesde);
    const hasta = dateSig(regla?.vigenteHasta);
    const condPrestacion = (regla?.condiciones || []).find(
      (c) => toSig(c?.atributo ?? c?.tipo) === "PRESTACIONCODIGO"
    );
    const codigoPrestacionSig = condPrestacion?.valor
      ? toSig(condPrestacion.valor)
      : toSig(basePrestacionFromRuleId(regla?.idRegla || regla?.ruleId));
    const planSig = String(regla?.plan || "")
      .split(",")
      .map((p) => planValueToPersist(p) ?? SIN_PLAN_VALUE)
      .map((p) => toSig(p))
      .filter(Boolean)
      .sort()
      .join(",");
    const condicionesSig = normalizarCondicionesParaFirma(regla?.condiciones || []);
    return [tipo, nombre, decision, prioridadSig, desde, hasta, codigoPrestacionSig, planSig, condicionesSig].join("||");
  };

  const idVersionActualEdicion = String(
    reglaParaEditar?.idReglaVersion ?? location.state?.reglaParaEditar?.idReglaVersion ?? ""
  );
  const buscarReglaDuplicadaExacta = (reglasFuente = reglasExistentes) => (reglasFuente || []).find((r) => {
    const sameSignature = construirFirmaReglaExistente(r) === firmaReglaActual;
    if (!sameSignature) return false;
    if (!modoEdicion) return true;
    const idVersionExistente = String(r?.idReglaVersion ?? "");
    if (idVersionActualEdicion && idVersionExistente) {
      return idVersionExistente !== idVersionActualEdicion;
    }
    // Si no tenemos id de versión, no excluimos por ruleId para no "taparnos" duplicados reales.
    return true;
  }) || null;

  const construirPayloadValidacionDuplicado = () => ({
    excludeIdRegla: String(ruleIdEdicion || reglaParaEditar?.ruleId || "").trim() || null,
    tipoReglaCodigo: String(tipoSeleccionado?.codigo || "PRESTACION").trim(),
    nombre: nombreRegla,
    decision: normalizeDecision(resultadoEsperado?.value),
    prioridad: Number(prioridad) || null,
    vigenteDesde,
    vigenteHasta: vigenteHasta || null,
    codigoPrestacion: esElegibilidad
      ? String(ruleIdBase || nombreRegla || "").trim() || null
      : String(idRegla?.value || "").trim() || null,
    idPlan: normalizarSeleccionPlanes(planesSeleccionados)
      .map((p) => planValueToPersist(p?.value) ?? SIN_PLAN_VALUE)
      .filter(Boolean)
      .sort()
      .join(",") || null,
    idPlanes: normalizarSeleccionPlanes(planesSeleccionados)
      .map((p) => planValueToPersist(p?.value))
      .filter(Boolean),
    condiciones: condicionesNormalizadas.map((cond) => ({
      atributo: cond.tipo,
      operador: cond.operador,
      valor: cond.valor,
    })),
  });

  const buildNextAvailableRule = (baseId, reglasFuente = reglasExistentes) => {
    const existentes = new Set(
      (reglasFuente || []).map((r) => String(r?.idRegla || r?.ruleId || ""))
    );
    let version = nextVersionForPrestacion(baseId, reglasFuente);
    let ruleId = `${baseId}_v${version}`;
    while (existentes.has(ruleId)) {
      version += 1;
      ruleId = `${baseId}_v${version}`;
    }
    return { version, ruleId };
  };

  const sanitizeElegibilidadKey = (value) =>
    String(value || "")
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .toUpperCase();

  const sanitizeRuleKey = (value) =>
    String(value || "")
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .toUpperCase();

  const condicionesBaseElegibilidad = condicionesNormalizadas.filter(
    (cond) =>
      String(cond?.tipo || "").toLowerCase() !== "prestacioncodigo" &&
      String(cond?.tipo || "").toLowerCase() !== "prerequisitos"
  );
  const atributoPrincipalElegibilidad = condicionesBaseElegibilidad[0]?.tipo || "";
  const nombreElegibilidadSugerido = atributoPrincipalElegibilidad
    ? `ELEG_${sanitizeElegibilidadKey(atributoPrincipalElegibilidad)}`
    : "";
  const reglaElegibilidadExistente =
    !modoEdicion && esElegibilidad && nombreElegibilidadSugerido
      ? (reglasExistentes || []).find((r) => {
          const nombre = String(r?.nombre || "").trim().toUpperCase();
          const descripcion = String(r?.descripcion || "").trim().toUpperCase();
          const buscado = nombreElegibilidadSugerido.toUpperCase();
          return nombre === buscado || descripcion === buscado;
        })
      : null;
  const baseElegibilidadExistente = reglaElegibilidadExistente
    ? basePrestacionFromRuleId(reglaElegibilidadExistente.idRegla || reglaElegibilidadExistente.ruleId)
    : "";
  const contextosPrioridad = useMemo(() => {
    const tipoReglaCodigo = String(tipoSeleccionado?.codigo || "").trim().toUpperCase();
    if (!tipoReglaCodigo) return [];

    const codigoPrestacionContexto =
      tipoReglaCodigo === "PRESTACION"
        ? String(idRegla?.value || "").trim() || null
        : null;

    const selected = normalizarSeleccionPlanes(planesSeleccionados);
    if (selected.length === 0) {
      return [{
        idPlan: null,
        label: "Sin plan asignado",
        tipoReglaCodigo,
        codigoPrestacion: codigoPrestacionContexto,
      }];
    }

    const unique = new Map();
    selected.forEach((plan) => {
      const idPlanPersistido = planValueToPersist(plan?.value);
      const key = idPlanPersistido ?? SIN_PLAN_VALUE;
      if (!unique.has(key)) {
        unique.set(key, {
          idPlan: idPlanPersistido,
          label: idPlanPersistido || "Sin plan asignado",
          tipoReglaCodigo,
          codigoPrestacion: codigoPrestacionContexto,
        });
      }
    });
    return Array.from(unique.values());
  }, [tipoSeleccionado?.codigo, idRegla?.value, planesSeleccionados]);

  useEffect(() => {
    const prioridadNumerica = Number(prioridad);
    if (!contextosPrioridad.length || !Number.isFinite(prioridadNumerica) || prioridadNumerica <= 0) {
      setPrioridadContextos([]);
      setPrioridadLoading(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setPrioridadLoading(true);
        const responses = await Promise.all(
          contextosPrioridad.map(async (ctx) => {
            if (ctx.tipoReglaCodigo === "PRESTACION" && !ctx.codigoPrestacion) {
              return {
                ...ctx,
                totalComparables: 0,
                reglasComparables: [],
                reglasConMismaPrioridad: [],
                reglaSuperiorInmediata: null,
                reglaInferiorInmediata: null,
              };
            }
            const response = await obtenerContextoPrioridadRegla({
              tipoReglaCodigo: ctx.tipoReglaCodigo,
              codigoPrestacion: ctx.codigoPrestacion || undefined,
              idPlan: ctx.idPlan || undefined,
              prioridad: prioridadNumerica,
              excludeIdRegla: modoEdicion ? String(ruleIdEdicion || reglaParaEditar?.ruleId || "") : undefined,
            });
            return { ...ctx, ...response };
          })
        );
            if (!cancelled) {
              setPrioridadContextos(
                responses.map((r) => ({
                  label: r.label,
                  tipoRegla: r.tipoRegla,
                  codigoPrestacion: r.codigoPrestacion,
                  totalComparables: r.totalComparables,
                  reglasComparables: r.reglasComparables,
                  reglasConMismaPrioridad: r.reglasConMismaPrioridad,
                  reglaSuperiorInmediata: r.reglaSuperiorInmediata,
                  reglaInferiorInmediata: r.reglaInferiorInmediata,
                }))
              );
        }
      } catch (error) {
        if (!cancelled) {
          logSafeError("warn", "No se pudo validar la prioridad en backend", error);
          setPrioridadContextos([]);
        }
      } finally {
        if (!cancelled) {
          setPrioridadLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [contextosPrioridad, prioridad, modoEdicion, ruleIdEdicion, reglaParaEditar?.ruleId]);

  const getErroresFormulario = () => {
    const errores = [];

    if (!esElegibilidad && !nombreRegla?.trim()) {
      errores.push("Debe completar el nombre de regla.");
    }
    if (!resultadoEsperado?.value) errores.push("Debe seleccionar una acción.");
    if (!tipoSeleccionado?.codigo) errores.push("Debe seleccionar el tipo de regla.");
    if (!prioridad || Number(prioridad) <= 0) errores.push("La prioridad debe ser mayor a 0.");
    if (!vigenteDesde) errores.push("La fecha de inicio de vigencia es obligatoria.");
    if (vigenteHasta && vigenteHasta < vigenteDesde) {
      errores.push("La fecha de fin de vigencia no puede ser anterior a la fecha de inicio.");
    }
    if (!tipoReglaData?.codigo) errores.push("No se pudo resolver el tipo de regla en catálogos.");
    if (!alcanceData?.nombre) errores.push("No se pudo resolver el alcance en catálogos.");

    if (!Array.isArray(condicionesNormalizadas) || condicionesNormalizadas.length === 0) {
      errores.push("Debe agregar al menos una condición.");
    } else {
      const condicionIncompleta = condicionesNormalizadas.some((cond) =>
        !String(cond?.tipo ?? "").trim() ||
        !String(cond?.operador ?? "").trim() ||
        String(cond?.valor ?? "").trim() === ""
      );
      if (condicionIncompleta) errores.push("Hay condiciones incompletas. Revise atributo, operador y valor.");
    }

    if (esElegibilidad) {
      if (!String(ruleIdBase || "").trim()) {
        errores.push("Debe agregar una condición de elegibilidad para generar la nomenclatura.");
      }
    }

    return errores;
  };

  const erroresFormulario = getErroresFormulario();
  const formularioInvalido = erroresFormulario.length > 0;

  // Cargar regla para editar o inicializar para alta
  useEffect(() => {
    if (!reglaParaEditar) {
      return;
    }

    setModoEdicion(true);
    setRuleIdEdicion(String(reglaParaEditar.ruleId || ""));
    setVersionEdicion(
      Number.isFinite(Number(reglaParaEditar.version)) ? Number(reglaParaEditar.version) : null
    );
    setNombreRegla(reglaParaEditar.nombre || "");
    setDescripcionRegla(reglaParaEditar.descripcion || reglaParaEditar.nombre || "");
    setPrioridad(reglaParaEditar.prioridad || "");
    setVigenteDesde(reglaParaEditar.vigenteDesde || hoyYYYYMMDD());
    setVigenteHasta(reglaParaEditar.vigenteHasta || "");
    setIdCobertura(reglaParaEditar.idCobertura ?? "");
    setIdItemCobertura(reglaParaEditar.idItemCobertura ?? "");
    setIdPack(reglaParaEditar.idPack ?? "");
    const planesRegla = planesReglaToValues(reglaParaEditar);
    const planesEncontrados = planesRegla
      .map((p) => (p === SIN_PLAN_VALUE ? SIN_PLAN_OPTION : planOptionsConSinPlan.find((opt) => opt.value === p)))
      .filter(Boolean);
    if (planesEncontrados.length > 0) {
      setPlanesSeleccionados(planesEncontrados);
    } else {
      setPlanesSeleccionados([]);
    }

    const prestacionCond = (reglaParaEditar.condiciones || []).find(
        (c) => String(c.atributo || "").toLowerCase() === "prestacioncodigo"
    );
    const baseId = prestacionCond?.valor
        ? String(prestacionCond.valor)
        : basePrestacionFromRuleId(reglaParaEditar.ruleId);
    const prestacionSeleccionada = prestacionCond?.valor
      ? practicas.find((opt) => String(opt.value) === String(baseId)) ||
        { value: baseId, label: String(baseId), descripcion: "" }
      : null;
    setIdRegla(prestacionSeleccionada);
    setRuleIdBase(String(baseId));

    const tipoFromReglaRaw = String(reglaParaEditar?.tipoRegla ?? "").trim().toUpperCase();
    const tipoFromRuleId = String(reglaParaEditar?.ruleId ?? "").toUpperCase().startsWith("ELEG_")
      ? "ELEGIBILIDAD"
      : "";
    const tipoFromNombre = String(reglaParaEditar?.nombre ?? "").toUpperCase().startsWith("ELEG_")
      ? "ELEGIBILIDAD"
      : "";
    const tipoFromCondicionPrestacion = (reglaParaEditar?.condiciones || []).some(
      (c) => String(c?.atributo || c?.tipo || "").toLowerCase() === "prestacioncodigo"
    )
      ? "PRESTACION"
      : "";
    const tipoInferido = tipoFromReglaRaw || tipoFromRuleId || tipoFromNombre || tipoFromCondicionPrestacion;
    const tipoElegido = tipoInferido === "ELEGIBILIDAD" ? "ELEGIBILIDAD" : "PRESTACION";
    setTipoSeleccionado({ id: 0, codigo: tipoElegido, nombre: tipoElegido === "ELEGIBILIDAD" ? "Elegibilidad" : "Prestación" });
    setTipoReglaData({ id: null, codigo: tipoElegido, desc: tipoElegido === "ELEGIBILIDAD" ? "Elegibilidad" : "Prestación" });
    const decisionNormalizada = resolverDecisionRegla(reglaParaEditar);
    const accionSeleccionada = resultadoOptions.find((opt) => opt.value === decisionNormalizada);
    setResultadoEsperado(accionSeleccionada || null);

    const esPrereq = !!reglaParaEditar.prerequisito;

    const condicionesMapeadas = dedupeCondiciones(
      (reglaParaEditar.condiciones || []).map((cond) => ({
        tipo: cond.atributo,
        operador: cond.operador,
        valor: cond.atributo === "prerequisitos"
          ? requisitoFallback(cond.valor ?? cond.valorComparacion)
          : (cond.valor ?? cond.valorComparacion),
      }))
    );
    if (esPrereq) {
      const yaExiste = condicionesMapeadas.some((c) => c.tipo === "prerequisitos");
      const motivo = requisitoFallback(reglaParaEditar.accion?.motivo);
      if (!yaExiste) {
        condicionesMapeadas.push({
          tipo: "prerequisitos",
          operador: "==",
          valor: motivo,
        });
      }
      const accionDerivar = resultadoOptions.find((opt) => opt.value === "DERIVAR");
      setResultadoEsperado(accionDerivar || null);
    }

    const condicionEdad = condicionesMapeadas.find(
      (c) => String(c?.tipo || "").toLowerCase() === "edad"
    );
    if (condicionEdad) {
      const { valor, unidad } = parsearDesdeDias(condicionEdad.valor, condicionEdad.operador);
      setValorNumericoEdad(valor);
      setUnidadEdad(unidad);
    } else {
      setValorNumericoEdad("");
      setUnidadEdad(UNIDADES_EDAD.DIAS);
    }

    setCondiciones(condicionesMapeadas);
  }, [reglaParaEditar, practicas]);

  const handleCrearRegla = async (options = {}) => {
    const usarInversa =
      typeof options === "boolean" ? options : Boolean(options?.usarInversa);
    const actualizarParejaModoEdicion = Boolean(options?.actualizarPareja) && modoEdicion;
    const accionInversaOverride = String(options?.accionInversa || "").trim();
    const modoAccionParejaOption = String(options?.modoAccionPareja || "mantener").trim().toLowerCase();
    const accionParejaOverride = String(options?.accionPareja || "").trim();
    setLoading(true);
    setMensaje(null);
    setInverseError(null);

    if (!vigenteDesde) {
      setMensaje({
        decision: "ERROR",
        motivos: ["La fecha de inicio de vigencia es obligatoria."],
        tipo: "no-elegible",
      });
      setLoading(false);
      return;
    }

    if (vigenteHasta && vigenteHasta < vigenteDesde) {
      setMensaje({
        decision: "ERROR",
        motivos: ["La fecha de fin de vigencia no puede ser anterior a la fecha de inicio."],
        tipo: "no-elegible",
      });
      setLoading(false);
      return;
    }

    const basePrestacion = esElegibilidad
      ? String(ruleIdBase || nombreElegibilidadSugerido).trim()
      : String(idRegla?.value || ruleIdBase || sanitizeRuleKey(nombreRegla || descripcionRegla || "REGLA_GLOBAL")).trim();

    if (!basePrestacion) {
      setMensaje({
        decision: "ERROR",
        motivos: ["No se pudo determinar el identificador base de la regla."],
        tipo: "no-elegible",
      });
      setLoading(false);
      return;
    }

    const versionEdicionActual = versionEdicion ?? Number(reglaParaEditar?.version) ?? 1;
    const ruleIdEdicionActual = String(ruleIdEdicion || reglaParaEditar?.ruleId || "");

    if (modoEdicion && !ruleIdEdicionActual) {
      setMensaje({
        decision: "ERROR",
        motivos: ["No se pudo resolver el identificador de la regla para actualizar."],
        tipo: "no-elegible",
      });
      setLoading(false);
      return;
    }

    const motivoAccion = nombreRegla;
    const decisionSeleccionada = resultadoEsperado?.value === "NO_REQUIERE_AUTORIZACION"
      ? "NO_REQUIERE_AUTORIZACION"
      : resultadoEsperado?.value;
    const accionSeleccionada = decisionSeleccionada;
    const accionIdBase = obtenerAccionId(accionSeleccionada);

    try {
      const reglasFuente = [];
      const usarInversaFinal = Boolean(usarInversa);
      const planesNormalizados = normalizarSeleccionPlanes(planesSeleccionados);
      const planes = planesNormalizados.length > 0
          ? planesNormalizados.map((plan) => ({
              option: plan,
              idPlan: planValueToPersist(plan?.value),
              idPlanes: planesValuesToPersist([plan]),
            }))
          : [{ option: null, idPlan: null, idPlanes: [] }];
      const errores = [];
      const exitos = [];
      const idsReservados = new Set(
        (reglasFuente || [])
          .map((r) => String(r?.idRegla || r?.ruleId || "").trim())
          .filter(Boolean)
      );
      let ultimoRuleIdGenerado = ruleIdEdicionActual;
      let ultimaVersionGenerada = versionEdicionActual;

      if (usarInversaFinal) {
        if (!inversaDisponible) {
          setMensaje({
            decision: "ERROR",
            motivos: ["No se puede generar la regla inversa para esta configuracion."],
            tipo: "no-elegible",
          });
          return;
        }
        if (!accionInversaOverride) {
          setInverseError("Debe seleccionar una acción para la regla inversa.");
          return;
        }
        const prioridadNumerica = Number(prioridad);
        if (!Number.isFinite(prioridadNumerica) || prioridadNumerica <= 1) {
          setInverseError("La prioridad base debe ser mayor a 1 para generar la inversa.");
          return;
        }
      }

      for (let planIndex = 0; planIndex < planes.length; planIndex += 1) {
        const planSeleccionado = planes[planIndex];
        const idPlan = planSeleccionado.idPlan;
        const idPlanes = Array.isArray(planSeleccionado.idPlanes) ? planSeleccionado.idPlanes : [];
        const actualizarReglaActual = modoEdicion && planIndex === 0;
        let versionActual = versionEdicionActual;
        let ruleIdActual = ruleIdEdicionActual;
        if (!actualizarReglaActual) {
          const candidato = buildNextAvailableRule(basePrestacion, reglasFuente);
          let versionCandidata = Number(candidato?.version) || 1;
          let ruleIdCandidato = String(candidato?.ruleId || `${basePrestacion}_v${versionCandidata}`).trim();
          while (idsReservados.has(ruleIdCandidato)) {
            versionCandidata += 1;
            ruleIdCandidato = `${basePrestacion}_v${versionCandidata}`;
          }
          versionActual = versionCandidata;
          ruleIdActual = ruleIdCandidato;
          idsReservados.add(ruleIdActual);
          if (usarInversaFinal) {
            idsReservados.add(`${ruleIdActual}_INV`);
          }
        }

        const alcancePayload = alcanceData?.nombre
          ? alcanceData
          : { idAlcance: null, nombre: "COBERTURA", descripcion: "" };
        const condicionesPayload = dedupeCondiciones(condiciones).map((cond) => {
          const tipoDato = obtenerTipoDato(cond.tipo);
          return {
            atributo: cond.tipo,
            operador: cond.operador,
            valor: normalizarValorPorTipoDato(cond.valor, tipoDato),
            tipoDato,
          };
        });
        const payload = {
          ruleId: ruleIdActual,
          version: versionActual,
          nombre: nombreRegla,
          descripcion: descripcionRegla || nombreRegla,
          prioridad: Number(prioridad),
          overwrite: actualizarReglaActual,
          status: "ACTIVO",
          vigenteDesde: vigenteDesde,
          vigenteHasta: vigenteHasta || null,

          codigoPrestacion: esElegibilidad
            ? String(basePrestacion || ruleIdBase || nombreRegla || "").trim()
            : idRegla?.value,
          idPlan: idPlan,
          idPlanes: idPlanes,
          idCobertura: normalizeOptionalLong(idCobertura),
          idItemCobertura: normalizeOptionalLong(idItemCobertura),
          idPack: normalizeOptionalLong(idPack),

          tipoRegla: {
            idTipoRegla: tipoReglaData.id,
            codigo: tipoReglaData.codigo,
            descripcion: tipoReglaData.desc
          },
          alcance: {
            idAlcance: alcancePayload.idAlcance ?? alcancePayload.id,
            nombre: alcancePayload.nombre,
            descripcion: alcancePayload.descripcion ?? alcancePayload.desc
          },

          prerequisito: prerequisitoActivo,
          condiciones: condicionesPayload,

          accion: {
            idAccion: accionIdBase,
            decision: accionSeleccionada,
            motivo: motivoAccion,
          },

          valorModificado: valorModificadoFinal || null
        };

        try {
          if (actualizarReglaActual) {
            await actualizarReglaPrestacion(String(ruleIdActual), payload);
          } else {
            await crearReglaPrestacion(payload);
          }

          if (actualizarParejaModoEdicion && guardarRelacion?.parejaRule) {
            const parejaRule = guardarRelacion.parejaRule;
            const parejaRuleId = String(guardarRelacion.parejaRuleId || parejaRule?.idRegla || parejaRule?.ruleId || "").trim();
            if (!parejaRuleId) {
              throw new Error("No se pudo resolver el ID de la regla relacionada para actualizar.");
            }

            const stripMarcaInversa = (text) =>
              String(text || "")
                .replace(/\s*\[AUTO_INVERSA_ABM\|BASE=[^\]]+\]\s*/g, "")
                .replace(/\s*\[INV\]\s*$/i, "")
                .trim();

            const condicionesPareja = (payload.condiciones || []).map((cond) => {
              return invertirCondicionNumerica(cond);
            });

            const decisionParejaActual = resolverDecisionRegla(parejaRule);
            const decisionPareja = modoAccionParejaOption === "elegir" && accionParejaOverride
              ? normalizeDecision(accionParejaOverride)
              : decisionParejaActual;
            if (!decisionPareja) {
              throw new Error(
                "No se pudo resolver la accion de la regla relacionada. Reabri la regla y selecciona una accion para la pareja."
              );
            }
            const accionIdPareja = obtenerAccionId(decisionPareja);
            if (!accionIdPareja) {
              throw new Error(
                `La accion '${decisionPareja}' de la regla relacionada no existe en catalogo.`
              );
            }
            const prioridadActual = Number(payload?.prioridad);
            const prioridadPareja = Number.isFinite(prioridadActual)
              ? (String(ruleIdActual).endsWith("_INV") ? prioridadActual + 1 : Math.max(0, prioridadActual - 1))
              : payload?.prioridad;

            const parejaEsInv = parejaRuleId.endsWith("_INV");
            const descripcionBase = stripMarcaInversa(payload?.descripcion || nombreRegla);
            const descripcionPareja = parejaEsInv
              ? `${descripcionBase} [INV]`
              : descripcionBase;

            const payloadPareja = {
              ...payload,
              ruleId: parejaRuleId,
              version: Number.isFinite(Number(parejaRule?.version)) ? Number(parejaRule.version) : payload.version,
              nombre: String(parejaRule?.nombre || payload?.nombre || ""),
              descripcion: descripcionPareja,
              prioridad: prioridadPareja,
              condiciones: condicionesPareja,
              accion: {
                ...(payload.accion || {}),
                idAccion: accionIdPareja,
                decision: decisionPareja,
                motivo: String(parejaRule?.accion?.motivo || payload?.accion?.motivo || ""),
              },
            };

            await actualizarReglaPrestacion(String(parejaRuleId), payloadPareja);
          }

          if (usarInversaFinal) {
            const inversaRuleId = `${ruleIdActual}_INV`;
            const inversaExiste = (reglasFuente || []).some(
              (r) =>
                String(r.idRegla || r.ruleId) === inversaRuleId ||
                String(r.descripcion || '').includes(`AUTO_INVERSA_ABM|BASE=${ruleIdActual}`)
            );

            if (!inversaExiste) {
              const accionInvertida = accionInversaOverride;
              const accionIdInversa = obtenerAccionId(accionInvertida);
              const prioridadInversa = Number(prioridad) - 1;

              const condicionesInversas = condiciones.map((cond) => {
                if (cond?.operador === "<" || cond?.operador === ">") {
                  // Complemento discreto:
                  // < N -> > N-1
                  // > N -> < N+1
                  return invertirCondicionNumerica(cond);
                }
                return cond;
              });
              const descripcionInversa = `${String(descripcionRegla || nombreRegla).replace(/\s*\[INV\]\s*$/i, "").trim()} [INV]`;

              const payloadInversa = {
                ...payload,
                ruleId: inversaRuleId,
                nombre: `${String(nombreRegla).replace(/\s*\[INV\]\s*$/i, "").trim()} [INV]`,
                descripcion: descripcionInversa,
                prioridad: prioridadInversa,
                condiciones: dedupeCondiciones(condicionesInversas).map((cond) => ({
                  atributo: cond.tipo,
                  operador: cond.operador,
                  valor: normalizarValorPorTipoDato(cond.valor, obtenerTipoDato(cond.tipo)),
                  tipoDato: obtenerTipoDato(cond.tipo),
                })),
                accion: {
                  idAccion: accionIdInversa,
                  decision: accionInvertida,
                  motivo: descripcionInversa
                }
              };

              try {
                await crearReglaPrestacion(payloadInversa);
              } catch (inverseErr) {
                await eliminarRegla(ruleIdActual);
                throw inverseErr;
              }
            }
          }

          ultimoRuleIdGenerado = ruleIdActual;
          ultimaVersionGenerada = versionActual;
          exitos.push(idPlan || "Sin plan asignado");
        } catch (err) {
          errores.push(`${idPlan || "Sin plan asignado"}: ${maskString(err.message || 'Error desconocido')}`);
        }
      }

      if (errores.length > 0) {
        setMensaje({
          decision: "ERROR",
          motivos: errores,
          tipo: "no-elegible",
        });
      } else {
        const elegibilidadVersionada =
          esElegibilidad && !modoEdicion && Boolean(baseElegibilidadExistente);
        const mensajeExito = modoEdicion
          ? "Regla actualizada exitosamente"
          : elegibilidadVersionada
            ? `Regla de elegibilidad existente versionada correctamente (${basePrestacion}_v${ultimaVersionGenerada})`
          : `Regla ${nombreRegla || ultimoRuleIdGenerado} creada correctamente para ${planes.length} plan(es)`;

        setMensaje({
          decision: "OK",
          motivos: [mensajeExito],
          tipo: "elegible",
        });
        setRuleIdEdicion(String(ultimoRuleIdGenerado || ""));
        setVersionEdicion(Number(ultimaVersionGenerada || 1));
      }
    } catch (error) {
      setMensaje({
        decision: "ERROR",
        motivos: [maskString(error.message || "Error al crear/actualizar la regla")],
        tipo: "no-elegible",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCrearRegla = async () => {
    setMensaje(null);
    setInverseError(null);

    if (formularioInvalido) {
      setMensaje({
        decision: "ERROR",
        motivos: erroresFormulario,
        tipo: "no-elegible",
      });
      return;
    }

    if (modoEdicion) {
      await abrirConfirmGuardar(false);
      return;
    }

    if (inversaDisponible) {
      const prioridadNumerica = Number(prioridad);
      if (!Number.isFinite(prioridadNumerica) || prioridadNumerica <= 1) {
        setInverseError("La prioridad base debe ser mayor a 1 para generar la inversa.");
        return;
      }
      setInverseError(null);
      setAccionInversaSeleccionada(opcionesAccionInversa[0] || null);
      setConfirmInversaOpen(true);
      return;
    }

    await abrirConfirmGuardar(false);
  };

  const handleSubmitCrearReglaConFeedback = async () => {
    const inicioPreparacion = Date.now();
    const minPreparacionMs = 1200;
    setSubmitPreparing(true);

    try {
      await handleSubmitCrearRegla();
    } finally {
      const restante = Math.max(0, minPreparacionMs - (Date.now() - inicioPreparacion));
      if (restante > 0) {
        await new Promise((resolve) => setTimeout(resolve, restante));
      }
      setSubmitPreparing(false);
    }
  };

  const handleConfirmInversa = (crearInversa, accionInversa) => {
    if (crearInversa && !String(accionInversa || "").trim()) {
      setInverseError("Debe seleccionar una acción para la regla inversa.");
      return;
    }
    setConfirmInversaOpen(false);
    setAccionInversaSeleccionada(null);
    setSubmitPreparing(true);
    (async () => {
      try {
        await abrirConfirmGuardar(crearInversa, accionInversa);
      } finally {
        setSubmitPreparing(false);
      }
    })();
  };

  const obtenerIdDeshabilitar = (regla) =>
    String(regla?.idReglaVersion ?? regla?.ruleId ?? regla?.idRegla ?? "").trim();

  const resolverRelacionInversa = (reglasFuente = null) => {
    const currentRuleId = String(ruleIdEdicion || reglaParaEditar?.ruleId || "").trim();
    return resolverRelacionInversaLocal({
      currentRuleId,
      reglaActual: reglaParaEditar,
      reglas: Array.isArray(reglasFuente) ? reglasFuente : reglasExistentes,
    });
  };

  const resolverRelacionInversaDesdeApi = async ({ signal } = {}) => {
    const currentRuleId = String(ruleIdEdicion || reglaParaEditar?.ruleId || "").trim();
    if (!currentRuleId) return null;

    const normalizarReglaApi = (r) => ({
      ...r,
      idRegla: r?.idRegla ?? r?.ruleId,
      ruleId: r?.idRegla ?? r?.ruleId,
    });

    try {
      const relacion = await obtenerRelacionInversaRegla(currentRuleId, { signal });
      const parejaRaw = relacion?.parejaRule;
      if (!parejaRaw) {
        const reglasFuente = await ensureReglasExistentes({ signal });
        return resolverRelacionInversa(reglasFuente);
      }
      const pareja = normalizarReglaApi(parejaRaw);
      return {
        currentRule: reglaParaEditar || null,
        parejaRule: pareja,
        currentRuleId: String(relacion?.currentRuleId || currentRuleId).trim(),
        parejaRuleId: String(relacion?.parejaRuleId || pareja?.idRegla || pareja?.ruleId || "").trim(),
        esActualInversa: Boolean(relacion?.esActualInversa),
        parejaActiva: Boolean(relacion?.parejaActiva),
      };
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      logSafeError("warn", "No se pudo resolver relación inversa", e);
      const reglasFuente = await ensureReglasExistentes({ signal });
      return resolverRelacionInversa(reglasFuente);
    }
  };

  const ejecutarInhabilitacion = async ({ incluirPareja = false } = {}) => {
    setLoading(true);
    setInhabilitarError("");
    setInhabilitarResultado(null);

    try {
      const currentRuleId = String(ruleIdEdicion || reglaParaEditar?.ruleId || "").trim();
      const idActual = String(reglaParaEditar?.idReglaVersion ?? currentRuleId).trim();
      if (!idActual) {
        throw new Error("No se encontró un identificador válido para inhabilitar la regla.");
      }

      const targets = [{ id: idActual, rid: currentRuleId || idActual }];
      if (incluirPareja && inhabilitarRelacion?.parejaRule) {
        const parejaId = obtenerIdDeshabilitar(inhabilitarRelacion.parejaRule);
        const parejaRid = inhabilitarRelacion.parejaRuleId || parejaId;
        if (parejaId && !targets.some((t) => t.id === parejaId || t.rid === parejaRid)) {
          targets.push({ id: parejaId, rid: parejaRid });
        }
      }

      const fallos = [];
      const ok = [];
      for (const t of targets) {
        try {
          await deshabilitarRegla(String(t.id));
          ok.push(t.rid);
        } catch (e) {
          fallos.push(`${t.rid}: ${e?.message || "Error al inhabilitar"}`);
        }
      }

      if (fallos.length > 0) {
        throw new Error(fallos.join(" | "));
      }

      setInhabilitarResultado({
        ok: true,
        title: ok.length > 1 ? "Reglas inhabilitadas con éxito" : "Regla inhabilitada con éxito",
        detail:
          ok.length > 1
            ? `Se inhabilitaron: ${ok.join(", ")}.`
            : "La regla quedó deshabilitada y no se aplicará en nuevas evaluaciones."
      });
    } catch (error) {
      const mensajeError = maskString(error.message || "Error al inhabilitar la regla");
      setInhabilitarError(mensajeError);
      setInhabilitarResultado({
        ok: false,
        title: "No se pudo inhabilitar la regla",
        detail: mensajeError
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrepararInhabilitacion = async () => {
    setInhabilitarError("");
    setInhabilitarResultado(null);
    const relacion = await resolverRelacionInversaDesdeApi();

    if (relacion?.parejaRule) {
      setInhabilitarRelacion(relacion);
      setModoInhabilitarRelacion(relacion?.parejaActiva ? "ambas" : "solo");
      setPasoInhabilitar("relation");
      return;
    }

    ejecutarInhabilitacion({ incluirPareja: false });
  };

  const handleAbrirModalInhabilitar = async () => {
    if (inhabilitarAbortRef.current) {
      inhabilitarAbortRef.current.abort();
    }
    inhabilitarAbortRef.current = new AbortController();
    const signal = inhabilitarAbortRef.current.signal;

    setConfirmInhabilitarOpen(true);
    setInhabilitarLoading(true);
    setInhabilitarError("");
    setInhabilitarResultado(null);
    setPasoInhabilitar("loading");

    try {
      const relacion = await resolverRelacionInversaDesdeApi({ signal });
      if (signal.aborted) return;
      if (relacion?.parejaRule) {
        setInhabilitarRelacion(relacion);
        setModoInhabilitarRelacion(relacion?.parejaActiva ? "ambas" : "solo");
        setPasoInhabilitar("relation");
      } else {
        setInhabilitarRelacion(null);
        setModoInhabilitarRelacion("solo");
        setPasoInhabilitar("confirm");
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      logSafeError("warn", "Error al resolver relación inversa", e);
      setPasoInhabilitar("confirm");
    } finally {
      if (!signal.aborted) {
        setInhabilitarLoading(false);
      }
    }
  };

  const cerrarModalInhabilitar = () => {
    if (loading) return;
    if (inhabilitarAbortRef.current) {
      inhabilitarAbortRef.current.abort();
      inhabilitarAbortRef.current = null;
    }
    setInhabilitarLoading(false);
    setInhabilitarError("");
    setInhabilitarResultado(null);
    setInhabilitarRelacion(null);
    setPasoInhabilitar("confirm");
    setModoInhabilitarRelacion("solo");
    setConfirmInhabilitarOpen(false);
  };

  const handleAgregarCondicion = () => {
    if (!tipoCondicion || !operador) return;
    if (esElegibilidad && tipoCondicion.value === "prerequisitos") return;
    if (tipoCondicion.value === "prerequisitos" && tieneCondicionPrerequisitos) return;

    const esEdad = String(tipoCondicion?.value || "").toLowerCase() === "edad";
    let valorFinal = valorCondicion;

    if (esEdad) {
      const dias = convertirADias(valorNumericoEdad, unidadEdad);
      if (dias === null) return;
      valorFinal = String(dias);
    } else if (esCondicionMedica) {
      const codigos = (condicionesMedicasSeleccionadas || [])
        .map((condicion) => String(condicion?.value || "").trim())
        .filter(Boolean);
      if (codigos.length === 0) return;
      valorFinal = JSON.stringify(codigos);

      setCondiciones((prev) => {
        const idx = prev.findIndex(
          (c) => String(c.tipo || "").toLowerCase() === CONDICION_MEDICA_ATTR
        );
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = {
            tipo: CONDICION_MEDICA_ATTR,
            operador: operador.value,
            valor: valorFinal,
          };
          return updated;
        }
        const nueva = {
          tipo: CONDICION_MEDICA_ATTR,
          operador: operador.value,
          valor: valorFinal,
        };
        const existentes = dedupeCondiciones(prev);
        const yaExiste = existentes.some(
          (cond) =>
            String(cond.tipo || "").toLowerCase() === String(nueva.tipo || "").toLowerCase() &&
            String(cond.operador || "") === String(nueva.operador || "") &&
            String(cond.valor || "") === String(nueva.valor || "")
        );
        return yaExiste ? prev : [...prev, nueva];
      });
      setTipoCondicion(null);
      setOperador(null);
      setValorCondicion("");
      setCondicionesMedicasSeleccionadas([]);
      setValorNumericoEdad("");
      setUnidadEdad(UNIDADES_EDAD.DIAS);
      setMostrarCondicion(false);
      return;
    } else if (!valorCondicion) {
      return;
    }

    const nuevaCondicion = {
      tipo: tipoCondicion.value,
      operador: operador.value,
      valor: valorFinal,
    };

    setCondiciones((prev) => {
      const existentes = dedupeCondiciones(prev);
      const yaExiste = existentes.some(
        (cond) =>
          String(cond.tipo || "").toLowerCase() === String(nuevaCondicion.tipo || "").toLowerCase() &&
          String(cond.operador || "") === String(nuevaCondicion.operador || "") &&
          String(cond.valor || "") === String(nuevaCondicion.valor || "")
      );
      const next = yaExiste ? prev : [...prev, nuevaCondicion];
      return next;
    });
    setTipoCondicion(null);
    setOperador(null);
    setValorCondicion("");
    setCondicionesMedicasSeleccionadas([]);
    setValorNumericoEdad("");
    setUnidadEdad(UNIDADES_EDAD.DIAS);
    setMostrarCondicion(false);
  };

  const handleAgregarReglaElegibilidadNoRequiere = (reglaElegibilidad) => {
    const condicionesFinales = construirCondicionesNoRequiereDesdeElegibilidad(reglaElegibilidad);
    if (condicionesFinales.length === 0) return;
    setCondiciones((prev) => dedupeCondiciones([...prev, ...condicionesFinales]));
  };

  const handleAgregarAtributoNoRequiere = () => {
    const atributo = String(atributoNoRequiere?.value || "").trim();
    const operadorSeleccionado = String((operadorForzadoNoRequiere || operadorNoRequiere)?.value || "").trim();
    const valor = String(valorNoRequiere || "").trim();
    if (!atributo || !operadorSeleccionado || !valor) return;

    setCondiciones((prev) =>
      dedupeCondiciones([
        ...prev,
        { tipo: atributo, operador: operadorSeleccionado, valor },
      ])
    );
    setAtributoNoRequiere(null);
    setOperadorNoRequiere(null);
    setValorNoRequiere("");
  };

  const handleEliminarCondicion = (index) => {
    setCondiciones((prev) => {
      const target = prev[index];
      if (!target) return prev;
      if (target?.tipo === "prestacionCodigo") return prev;
      if (esCondicionNoRequiereNoEditable(target)) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleCancelarCondicion = () => {
    setTipoCondicion(null);
    setOperador(null);
    setValorCondicion("");
    setCondicionesMedicasSeleccionadas([]);
    setValorNumericoEdad("");
    setUnidadEdad(UNIDADES_EDAD.DIAS);
    setMostrarCondicion(false);
  };

  const handleLimpiar = () => {
    setNombreRegla("");
    setIdRegla(null);
    setRuleIdBase("");
    setVigenteDesde(hoyYYYYMMDD());
    setVigenteHasta("");
    setResultadoEsperado(null);
    setCondiciones([]);
    setModoEdicion(false);
    setMensaje(null);
    setPrioridad(null);

    // Resetear nuevos estados
    setDescripcionRegla("");
    setPlanesSeleccionados([]);
    setIdCobertura(null);
    setIdItemCobertura(null);
    setIdPack(null);
    setTipoReglaData({ id: null, codigo: 'PRESTACION', desc: 'Prestación' });
    setAlcanceData({ id: null, nombre: '', desc: '' });
    setValorModificadoFinal(null);
    setCobertura({ id: null });
    setItem({ id: null });
    setPack({ id: null });
    setTipoSeleccionado({ id: 0, codigo: 'PRESTACION', nombre: 'Prestación' });
    setAlcance({ id: 0, nombre: '', descripcion: '' });
    setValorModificadoCalculado(null);
    setInverseError(null);
    setInhabilitarError("");
    setInhabilitarRelacion(null);
    setPasoInhabilitar("confirm");
    setModoInhabilitarRelacion("solo");
    setAccionInversaSeleccionada(null);
    setAccionInversaPendiente("");
    setConfirmInversaOpen(false);
    setConfirmInhabilitarOpen(false);
    setConfirmGuardarOpen(false);
    setConfirmGuardarLoading(false);
    setUsarInversaPendiente(false);
    setRuleIdEdicion("");
    setVersionEdicion(null);
    setValorNumericoEdad("");
    setUnidadEdad(UNIDADES_EDAD.DIAS);
  };

  const handleVolver = () => {
    if (loading || submitPreparing || inhabilitarLoading || confirmInversaOpen || confirmGuardarOpen || confirmGuardarLoading) {
      return;
    }
    navigate("/rulesView");
  };

  const abrirConfirmGuardar = async (usarInversa = false, accionInversa = "", relacionForzada = null) => {
    setMensaje(null);
    setUsarInversaPendiente(Boolean(usarInversa));
    setAccionInversaPendiente(String(accionInversa || "").trim());
    setActualizarConPareja(false);
    setModoAccionPareja("mantener");
    setAccionParejaSeleccionada(null);
    setAccionParejaError("");
    setGuardarRelacion(null);
    setConfirmGuardarOpen(true);
    setConfirmGuardarLoading(true);
    try {
      const duplicado = await validarDuplicadoExactoRegla(construirPayloadValidacionDuplicado());
      const reglaDuplicadaEnGuardado = duplicado?.duplicado ? duplicado?.reglaDuplicada : null;
      if (reglaDuplicadaEnGuardado) {
        setMensaje({
          decision: "ERROR",
          motivos: [
            `Ya existe una regla con las mismas caracteristicas (${String(
              reglaDuplicadaEnGuardado?.idRegla || reglaDuplicadaEnGuardado?.ruleId || "sin ID"
            )}).`,
            "Modifica algun dato (condiciones, vigencia, prioridad o accion) para poder continuar."
          ],
          tipo: "no-elegible",
        });
        return;
      }
      const relacion = relacionForzada || await resolverRelacionInversaDesdeApi();
      setGuardarRelacion(relacion?.parejaRule ? relacion : null);
    } catch (error) {
      logSafeError("warn", "No se pudo preparar la validacion de actualizacion", error);
      setMensaje({
        decision: "ERROR",
        motivos: ["No se pudo validar la regla. Intenta nuevamente."],
        tipo: "no-elegible",
      });
    } finally {
      setConfirmGuardarLoading(false);
    }
  };

  const cerrarConfirmGuardar = () => {
    if (loading || confirmGuardarLoading) return;
    setConfirmGuardarOpen(false);
    setConfirmGuardarLoading(false);
    setUsarInversaPendiente(false);
    setAccionInversaPendiente("");
    setActualizarConPareja(false);
    setModoAccionPareja("mantener");
    setAccionParejaSeleccionada(null);
    setAccionParejaError("");
    setGuardarRelacion(null);
    setMensaje(null);
  };

  const handleConfirmarGuardar = () => {
    if (actualizarConPareja && modoAccionPareja === "elegir") {
      const decisionElegida = normalizeDecision(accionParejaSeleccionada?.value || "");
      const decisionActual = normalizeDecision(resultadoEsperado?.value || "");
      if (!decisionElegida) {
        setAccionParejaError("Selecciona una accion para la regla relacionada.");
        return;
      }
      if (decisionElegida === decisionActual) {
        setAccionParejaError("La regla relacionada no puede quedar con la misma accion que la regla actual.");
        return;
      }
      if (decisionElegida === accionParejaActual) {
        setAccionParejaError("Esa accion ya esta configurada en la regla relacionada. Elegi otra.");
        return;
      }
      setAccionParejaError("");
    }

    handleCrearRegla({
      usarInversa: usarInversaPendiente,
      accionInversa: accionInversaPendiente,
      actualizarPareja: actualizarConPareja,
      modoAccionPareja,
      accionPareja: accionParejaSeleccionada?.value || "",
    });
  };

  const handleMensajeVolverLista = () => {
    setConfirmGuardarOpen(false);
    setUsarInversaPendiente(false);
    setMensaje(null);
    navigate("/rulesView");
  };

  const handleMensajeModificarRegla = () => {
    if (!modoEdicion && resultadoModalEstado === "success" && ruleIdEdicion) {
      setModoEdicion(true);
    }
    setConfirmGuardarOpen(false);
    setUsarInversaPendiente(false);
    setMensaje(null);
  };

  const handleChangeNombre = (e) => {
    if (esElegibilidad && !modoEdicion) return;
    const value = e.target.value;
    setNombreRegla(value);
    setDescripcionRegla(value);
  };

  useEffect(() => {
    if (!esElegibilidad || modoEdicion) return;
    if (!nombreElegibilidadSugerido) {
      setNombreRegla("");
      setRuleIdBase("");
      return;
    }

    setNombreRegla(nombreElegibilidadSugerido);
    setRuleIdBase(baseElegibilidadExistente || nombreElegibilidadSugerido);
  }, [esElegibilidad, modoEdicion, nombreElegibilidadSugerido, baseElegibilidadExistente]);

  // Auto-configurar operador y resultado cuando se selecciona prerequisitos
  useEffect(() => {
    if (tipoCondicion?.value === "prerequisitos") {
      const operadorIgual = operadorOptions.find((opt) => opt.value === "==");
      setOperador(operadorIgual);

      const accionDerivar = resultadoOptions.find((opt) => opt.value === "DERIVAR");
      setResultadoEsperado(accionDerivar);
    } else if (tipoCondicion?.value === CONDICION_MEDICA_ATTR) {
      const operadorIn = operadorOptions.find(
        (opt) => String(opt.value || "").toUpperCase() === "IN" || String(opt.value || "").toUpperCase() === "EN"
      );
      setOperador(operadorIn || null);

      const existente = condiciones.find(
        (c) => String(c.tipo || "").toLowerCase() === CONDICION_MEDICA_ATTR
      );
      if (existente) {
        const codigos = parseCondicionMedicaValor(existente.valor);
        const seleccionadas = codigos
          .map((codigo) => condicionesMedicasCatalogo.find((c) => String(c.value || "").trim() === String(codigo).trim()))
          .filter(Boolean);
        setCondicionesMedicasSeleccionadas(seleccionadas);
      } else {
        setCondicionesMedicasSeleccionadas([]);
      }
    }
  }, [tipoCondicion]);

  // Cargar practicas desde nomenclador (busqueda por codigo o descripcion)
  const cargarOpciones = async (inputValue) => {
    const query = String(inputValue || "").trim();
    if (query.length < 2) {
      return [];
    }

    try {
      const numericQuery = /^[0-9.]+$/.test(query);
      const [dataPracticas, dataMedicamentos] = await Promise.all([
        buscarPracticas({
          codigo: numericQuery ? query : undefined,
          descripcion: numericQuery ? undefined : query,
          page: 0,
          size: 30,
        }),
        // Medicamentos viven en un catalogo aparte (codigo 98xxxxxx), no en /v1/items.
        buscarMedicamentos({ q: query, page: 0, size: 30 }).catch((e) => {
          logSafeError("warn", "Error buscando medicamento", e);
          return null;
        }),
      ]);
      const items = Array.isArray(dataPracticas?.content) ? dataPracticas.content : [];
      const medicamentos = Array.isArray(dataMedicamentos?.content) ? dataMedicamentos.content : [];

      const opciones = [
        ...items.map(buildPracticaOption),
        ...medicamentos.map(buildMedicamentoOption),
      ].filter(Boolean);
      const porCodigoNormalizado = new Map();
      opciones.forEach((opt) => {
        const codigo = String(opt.value ?? "");
        const clave = codigo.replace(/^0+/, "") || codigo;
        const previo = porCodigoNormalizado.get(clave);
        if (!previo || codigo.length > String(previo.value).length) {
          porCodigoNormalizado.set(clave, opt);
        }
      });
      return Array.from(porCodigoNormalizado.values());
    } catch (e) {
      logSafeError("warn", "Error buscando practica medica", e);
      return [];
    }
  };

  useEffect(() => {
    if (esElegibilidad) return;
    if (!modoEdicion) return;
    const codigo = String(idRegla?.value || "").trim();
    if (!codigo) return;

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const data = await buscarItemsPorCodigo(codigo, {
          page: 0,
          size: 20,
          signal: controller.signal,
        });

        if (cancelled) return;

        const items = Array.isArray(data?.content) ? data.content : [];
        const item =
          items.find((i) => String(i?.codigoItemCobertura ?? i?.code ?? i?.codigo ?? "") === codigo) ||
          items[0];

        const option = buildPracticaOption(item);
        if (option) {
          setIdRegla(option);
        }
      } catch (error) {
        if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
          logSafeError("warn", "No se pudo resolver la descripción de la prestación", error);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [idRegla?.value, esElegibilidad, modoEdicion]);
  // Mantener prestacionCodigo sincronizado siempre al cambiar la prestación
  useEffect(() => {
    if (esElegibilidad) return;
    if (modoEdicion) return;
    if (!idRegla?.value) return;
    const prestacionCodigo = String(idRegla.value);
    setCondiciones((prev) => {
      const withoutPrestacion = (prev || []).filter((c) => c?.tipo !== "prestacionCodigo");
      const next = dedupeCondiciones([
        { tipo: "prestacionCodigo", operador: "==", valor: prestacionCodigo },
        ...withoutPrestacion
      ]);
      return next;
    });
  }, [idRegla?.value, esElegibilidad, modoEdicion]);

  return (
      <div className="page-content">
        <div className="controls-section">
          <div className="flex flex-col bg-white rounded-2xl shadow-sm w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                {modoEdicion ? "Editar Regla" : "Alta de Regla"}
              </h2>

              <button
                onClick={handleVolver}
                disabled={loading || submitPreparing || inhabilitarLoading || confirmInversaOpen || confirmGuardarOpen}
                className="text-[#B04708] hover:text-[#8a3606] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                title={(loading || submitPreparing || inhabilitarLoading || confirmInversaOpen || confirmGuardarOpen) ? "Hay una operación en curso" : undefined}
              >
                ← Volver a la lista
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Descripción</label>
                  <input
                      type="text"
                      value={nombreRegla}
                      onChange={handleChangeNombre}
                      disabled={!modoEdicion && esElegibilidad}
                      placeholder="Ingrese descripción..."
                      className="font-sans py-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B04708] focus:border-transparent transition duration-150 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                  />
                  {!modoEdicion && esElegibilidad && (
                    <span className="text-xs text-gray-500 mt-1">
                      Se define automáticamente con nomenclatura: ELEG_[ATRIBUTO].
                    </span>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Ámbito</label>
                  <Select
                      options={ambitoOptions}
                      value={ambitoOptions[0]}
                      onChange={() => {}}
                      placeholder="Seleccionar..."
                      styles={customStyles}
                      isDisabled
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Tipo de regla</label>
                  <Select
                      options={tipoOptions.filter((opt) => opt.value === "PRESTACION")}
                      value={tipoOptions.find((opt) => opt.value === "PRESTACION")}
                      isDisabled={true}
                      isSearchable={false}
                      onChange={(selected) => {
                        if (!selected) return;
                        const codigo = selected.value;
                        setTipoSeleccionado({ id: 0, codigo, nombre: selected.label });
                        setTipoReglaData({ id: null, codigo, desc: selected.label });
                        if (codigo === "ELEGIBILIDAD") {
                          setIdRegla(null);
                          setCondiciones((prev) =>
                            (prev || []).filter(
                              (c) => c?.tipo !== "prestacionCodigo" && c?.tipo !== "prerequisitos"
                            )
                          );
                          setPlanesSeleccionados([SIN_PLAN_OPTION]);
                          setResultadoEsperado(null);
                          setTipoCondicion(null);
                          setOperador(null);
                          setValorCondicion("");
                          setValorNumericoEdad("");
                          setUnidadEdad(UNIDADES_EDAD.DIAS);
                          setMostrarCondicion(false);
                        } else {
                          setRuleIdBase("");
                        }
                      }}
                      placeholder="Seleccionar..."
                      styles={customStyles}
                      isClearable={false}
                  />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-3">Aplicación <span className="text-red-500">*</span></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">Asociado</label>
                        <input
                          type="text"
                          value={idCobertura ?? ''}
                          onChange={(e) => setIdCobertura(e.target.value)}
                          placeholder="Sin asociado"
                          disabled={esNoRequiereAutorizacion}
                          className="font-sans py-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B04708] focus:border-transparent transition duration-150 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">Plan(es) de Salud</label>
                        <Select
                          options={planOptions}
                          value={planesSeleccionados}
                          onChange={(selected) => setPlanesSeleccionados(normalizarSeleccionPlanes(selected))}
                          placeholder="-"
                          styles={customStyles}
                          isMulti
                          isClearable
                          isDisabled={esNoRequiereAutorizacion}
                        />
                        <span className="text-xs text-gray-500 mt-1">Si no seleccionas ninguno, la regla se guarda sin plan asignado.</span>
                      </div>

                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">Pack</label>
                        <input
                          type="text"
                          value={idPack ?? ''}
                          onChange={(e) => setIdPack(e.target.value)}
                          placeholder="None"
                          disabled={esNoRequiereAutorizacion}
                          className="font-sans py-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B04708] focus:border-transparent transition duration-150 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">
                          {esElegibilidad ? "Nomenclatura de Elegibilidad" : "Prestación Individual"}
                        </label>
                        {esElegibilidad ? (
                          <>
                            <input
                              type="text"
                              value={ruleIdBase || "Definí una condición para generar la nomenclatura"}
                              readOnly
                              className="font-sans py-2 px-4 rounded-md border border-gray-300 bg-gray-100 cursor-not-allowed"
                            />
                            <span className="text-xs text-gray-500 mt-1">
                              No aplica código de prestación para reglas de elegibilidad.
                            </span>
                            {ruleIdBase && <span className="text-xs text-gray-500 mt-1">Rule ID base: {ruleIdBase}</span>}
                            {reglaElegibilidadExistente && (
                              <span className="text-xs text-amber-700 mt-1">
                                Ya existe una regla de elegibilidad con este nombre. Se creará una nueva versión.
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            <AsyncSelect
                              cacheOptions
                              loadOptions={cargarOpciones}
                              formatOptionLabel={formatPracticaOption}
                              value={idRegla}
                              onChange={(selected) => {
                                setIdRegla(selected);
                                setRuleIdBase(selected?.value ? String(selected.value) : "");
                              }}
                              placeholder="Buscá por código o descripción"
                              noOptionsMessage={({ inputValue }) =>
                                String(inputValue || "").trim().length < 2
                                  ? "Escribi al menos 2 caracteres"
                                  : "No se encontraron resultados"
                              }
                              styles={customStyles}
                              isClearable
                              isDisabled={modoEdicion || esNoRequiereAutorizacion}
                              defaultOptions={false}
                            />
                            {idRegla?.value && (
                              <span className="text-xs text-gray-500 mt-1">
                                {idRegla?.descripcion
                                  ? `Prestacion seleccionada: ${idRegla.value} - ${idRegla.descripcion}`
                                  : `Codigo de prestacion: ${idRegla.value}`}
                                {idRegla?.fueraDePmo && (
                                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                                    Fuera de PMO
                                  </span>
                                )}
                              </span>
                            )}
                            {!modoEdicion && !esNoRequiereAutorizacion && (
                              <button
                                type="button"
                                onClick={abrirModalPracticaNueva}
                                className="text-xs text-blue-600 hover:underline mt-1 self-start"
                              >
                                ¿No encontrás la práctica? Cargala como fuera de PMO
                              </button>
                            )}
                            {!idRegla?.value && (
                              <span className="text-xs text-gray-500 mt-1">
                                Si no seleccionas ninguna, la regla se guarda sin prestacion asignada.
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">Acción</label>
                        <Select
                            options={resultadoOptions}
                            value={resultadoEsperado}
                            onChange={(selected) => setResultadoEsperado(selected)}
                            placeholder="-"
                            styles={customStyles}
                            isClearable
                            isDisabled={esNoRequiereAutorizacion || !hayPlanElegido}
                        />
                        {esNoRequiereAutorizacion ? (
                          <span className="text-xs text-gray-500 mt-1">
                            Accion tecnica bloqueada para la regla de No Requiere.
                          </span>
                        ) : !hayPlanElegido ? (
                          <span className="text-xs text-gray-500 mt-1">
                            Elegí primero el plan: de eso depende qué acciones se pueden aplicar.
                          </span>
                        ) : hayPlanFueraPmo ? (
                          <span className="text-xs text-gray-500 mt-1">
                            Fuera del PMO la prestación no tiene cobertura definida, así que siempre deriva a auditoría.
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                  <input
                      type="text"
                      value={prioridad ?? ""}
                      onChange={handleChangePrioridad}
                      placeholder="Ingrese prioridad"
                      readOnly={esNoRequiereAutorizacion}
                      className="font-sans py-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B04708] focus:border-transparent read-only:bg-gray-100 read-only:cursor-not-allowed"
                      required
                  />
                  {prioridadLoading && (
                    <span className="text-xs text-gray-500 mt-2">Validando prioridad contra reglas comparables...</span>
                  )}
                  {!prioridadLoading && prioridadContextos.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {prioridadContextos.map((ctx) => {
                        const mismas = Array.isArray(ctx?.reglasConMismaPrioridad) ? ctx.reglasConMismaPrioridad : [];
                        return (
                          <div key={`${ctx.tipoRegla}-${ctx.label}`} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                            <div className="font-semibold text-gray-800">
                              Contexto: {ctx.label}{ctx.codigoPrestacion ? ` · ${ctx.codigoPrestacion}` : ""}
                            </div>
                            <div>{ctx.totalComparables || 0} regla(s) comparable(s).</div>
                            {mismas.length > 0 ? (
                              <div className="mt-1 text-red-700">
                                Misma prioridad: {mismas.map((item) => `${item.idRegla} (${item.prioridad})`).join(", ")}
                              </div>
                            ) : (
                              <div className="mt-1 text-emerald-700">No hay otra regla con esta misma prioridad.</div>
                            )}
                            {ctx.reglaSuperiorInmediata && (
                              <div className="mt-1">
                                Por arriba: {ctx.reglaSuperiorInmediata.idRegla} ({ctx.reglaSuperiorInmediata.prioridad})
                              </div>
                            )}
                            {ctx.reglaInferiorInmediata && (
                              <div>
                                Por abajo: {ctx.reglaInferiorInmediata.idRegla} ({ctx.reglaInferiorInmediata.prioridad})
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Fecha inicio vigencia</label>
                  <input
                      type="date"
                      value={vigenteDesde}
                      onChange={(e) => setVigenteDesde(e.target.value)}
                      className={`font-sans py-2 px-4 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#B04708] focus:border-transparent transition duration-150 ${fechaInicioInvalida ? "border-red-400" : "border-gray-300"}`}
                  />
                  <span className="text-xs text-gray-500 mt-1">Campo obligatorio.</span>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Fecha fin vigencia</label>
                  <input
                      type="date"
                      value={vigenteHasta}
                      onChange={(e) => setVigenteHasta(e.target.value)}
                      className={`font-sans py-2 px-4 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#B04708] focus:border-transparent transition duration-150 ${fechaFinInvalida ? "border-red-400" : "border-gray-300"}`}
                  />
                  <span className="text-xs text-gray-500 mt-1">Campo opcional. Si se informa, debe ser mayor o igual al inicio.</span>
                </div>

                {!esNoRequiereAutorizacion && (
                <div className="flex flex-col md:col-span-2">
                  <button
                      type="button"
                      onClick={() => setMostrarCondicion((prev) => !prev)}
                      className="text-sm text-[#B04708] font-medium hover:underline self-start"
                  >
                    + Agregar una condición
                  </button>
                </div>
                )}
              </div>
            </div>

            {esNoRequiereAutorizacion && (
              <div className="px-6 pb-4">
                <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                      <div>
                        <h3 className="text-sm font-semibold text-teal-900">Regla tecnica de No Requiere Autorizacion</h3>
                        <p className="text-xs text-teal-800 mt-1">
                          Las condiciones se cargan con el valor final que evalua No Requiere. No se invierten al guardar.
                        </p>
                      </div>

                      <div className="text-xs text-gray-700 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                        <div className="font-semibold text-amber-900 mb-1">Condiciones tecnicas obligatorias no editables</div>
                        {condicionesNoRequiereObligatoriasVisibles.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {condicionesNoRequiereObligatoriasVisibles.map((cond, idx) => (
                              <span key={`${cond.tipo}-${idx}`} className="rounded-full bg-white border border-amber-100 px-2 py-1">
                                {cond.displayText || `${cond.tipo} ${cond.operador} ${String(cond.valor)}`}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-amber-800">Todavia no hay condiciones obligatorias cargadas.</span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">Agregar desde elegibilidad</label>
                        <Select
                          options={(reglasElegibilidadCatalogo || []).map((regla) => ({
                            value: String(regla?.ruleId || regla?.idRegla || ""),
                            label: formatearReglaElegibilidad(regla),
                            regla,
                          }))}
                          value={null}
                          onChange={(selected) => handleAgregarReglaElegibilidadNoRequiere(selected?.regla)}
                          styles={customStyles}
                          placeholder={reglasElegibilidadLoading ? "Cargando reglas..." : "Buscar regla de elegibilidad"}
                          isDisabled={reglasElegibilidadLoading}
                          noOptionsMessage={() => "Sin reglas de elegibilidad"}
                        />
                      </div>

                      <div className="text-xs text-gray-700 rounded-md border border-teal-100 bg-white px-3 py-2">
                        <div className="font-semibold text-gray-800 mb-1">Condiciones tecnicas visibles</div>
                        {condicionesNoRequiereSistema.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {condicionesNoRequiereSistema.map((cond, idx) => (
                              <span key={`${cond.tipo}-${idx}`} className="rounded-full bg-gray-100 px-2 py-1">
                                {cond.tipo} {cond.operador} {String(cond.valor)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">Todavia no hay condiciones tecnicas cargadas.</span>
                        )}
                      </div>
                    </div>

                    <div className="rounded-md border border-teal-100 bg-white px-3 py-3">
                      <div className="text-sm font-semibold text-gray-800 mb-3">Agregar atributo de base</div>
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-0 flex flex-col">
                          <label className="text-xs font-medium text-gray-600 mb-1">Atributo</label>
                          <Select
                            options={atributoBaseNoRequiereOptions}
                            value={atributoNoRequiere}
                            onChange={(selected) => {
                              setAtributoNoRequiere(selected);
                              setOperadorNoRequiere(null);
                            }}
                            styles={customStyles}
                            placeholder="Seleccionar"
                            noOptionsMessage={() => "Sin atributos"}
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col">
                          <label className="text-xs font-medium text-gray-600 mb-1">Operador</label>
                          <Select
                            options={operadorOptions}
                            value={operadorForzadoNoRequiere || operadorNoRequiere}
                            onChange={setOperadorNoRequiere}
                            styles={customStyles}
                            placeholder="-"
                            isDisabled={operadorNoRequiereDisabled}
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col">
                          <label className="text-xs font-medium text-gray-600 mb-1">Valor</label>
                          <input
                            type="text"
                            value={valorNoRequiere}
                            onChange={(e) => setValorNoRequiere(e.target.value)}
                            className="py-2 px-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-[#B04708]"
                            placeholder="Ej: WEB,APP / false"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAgregarAtributoNoRequiere}
                          className="bg-[#B04708] hover:bg-[#8a3606] text-white text-sm py-2 px-4 rounded-md flex-shrink-0"
                        >
                          Agregar
                        </button>
                      </div>
                    </div>

                    {condicionesNoRequiereBase.length > 0 && (
                      <div className="text-xs text-gray-700">
                        Atributos de base existentes:{" "}
                        {condicionesNoRequiereBase.map((cond) => `${cond.tipo} ${cond.operador} ${cond.valor}`).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {condiciones.length > 0 && (
              <div className="px-6 pb-2">
                <div className="mt-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Condiciones aplicadas</h3>
                  {mostrarInfoOrdenMedica && (
                    <div className="mb-3 inline-flex max-w-full items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-sky-900 shadow-sm">
                      <span
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-600 text-[11px] font-bold text-white"
                        aria-hidden="true"
                      >
                        i
                      </span>
                      <p className="truncate text-sm text-sky-800">
                        <span className="font-semibold">Requisito informado:</span>{" "}
                        <span className="font-medium">Orden médica</span>
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {condiciones.map((cond, index) => {
                      if (esCondicionNoRequiereNoEditable(cond)) {
                        return null;
                      }
                      if (
                        mostrarInfoOrdenMedica &&
                        String(cond?.tipo || "").toLowerCase() === "prerequisitos" &&
                        isOrdenMedicaRequirement(cond?.valor)
                      ) {
                        return null;
                      }
                      return (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-[#fff2f0] border border-[#B04708] text-[#6b2c05] px-3 py-2 rounded-lg text-sm shadow-sm"
                      >
                        {String(cond?.tipo || "").toLowerCase() === "edad" ? (
                          <span className="font-semibold">{renderCondicionEdad(cond.valor, cond.operador)}</span>
                        ) : cond?.tipo === CONDICION_MEDICA_ATTR ? (
                          <>
                            <span className="font-medium">Condicion Medica</span>
                            <span>{cond.operador}</span>
                            <span className="font-semibold">{parseCondicionMedicaValor(cond.valor).join(", ")}</span>
                          </>
                        ) : (
                          <>
                            <span className="font-medium">
                              {cond.tipo === "prerequisitos" ? "Requisitos" : cond.tipo}
                            </span>
                            <span>{cond.operador}</span>
                            <span className="font-semibold">{cond.tipo === "prerequisitos" ? normalizarRequisitos(cond.valor) : cond.valor}</span>
                          </>
                        )}
                        <button
                          onClick={() => handleEliminarCondicion(index)}
                          className="ml-2 text-red-600 hover:text-red-800 font-bold"
                          title="Eliminar condición"
                          disabled={cond.tipo === "prestacionCodigo" || esCondicionNoRequiereNoEditable(cond)}
                        >
                          ×
                        </button>
                      </div>
                    )})}
                  </div>
                </div>
              </div>
            )}

            {!esNoRequiereAutorizacion && mostrarCondicion && (
                <div className="px-6 pb-4">
                  <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">Atributo</label>
                        <SelectAtributo
                            value={tipoCondicion}
                            onChange={setTipoCondicion}
                            customStyles={customStyles}
                            disablePrerequisitos={tieneCondicionPrerequisitos || esElegibilidad}
                            tipoReglaCodigo={tipoSeleccionado?.codigo || "PRESTACION"}
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">Comparación</label>
                        <Select
                            options={operadorOptions}
                            value={operador}
                            onChange={setOperador}
                            styles={customStyles}
                              placeholder="-"
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">
                          {esPrerequisitos ? "Motivos" : "Valor"}
                        </label>
                        {String(tipoCondicion?.value || "").toLowerCase() === "edad" ? (
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={valorNumericoEdad}
                              onChange={(e) => setValorNumericoEdad(e.target.value)}
                              className="py-2 px-4 rounded-md border border-gray-300 focus:ring-2 focus:ring-[#B04708] w-1/2"
                              placeholder="Ej: 12"
                            />
                            <select
                              value={unidadEdad}
                              onChange={(e) => setUnidadEdad(e.target.value)}
                              className="py-2 px-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-[#B04708] w-1/2"
                            >
                              <option value={UNIDADES_EDAD.DIAS}>Dias</option>
                              <option value={UNIDADES_EDAD.MESES}>Meses</option>
                              <option value={UNIDADES_EDAD.ANIOS}>Años</option>
                            </select>
                          </div>
                        ) : esCondicionMedica ? (
                          <Select
                            isMulti
                            options={condicionesMedicasCatalogo}
                            value={condicionesMedicasSeleccionadas}
                            onChange={(values) => setCondicionesMedicasSeleccionadas(values || [])}
                            styles={customStyles}
                            placeholder="Buscar condicion medica"
                            noOptionsMessage={() => "Sin condiciones disponibles"}
                          />
                        ) : (
                          <input
                              type="text"
                              value={valorCondicion}
                              onChange={(e) => setValorCondicion(e.target.value)}
                              className="py-2 px-4 rounded-md border border-gray-300 focus:ring-2 focus:ring-[#B04708]"
                              placeholder={esPrerequisitos ? "Ingrese motivos" : "Ej: 18 / true / texto"}
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-4 gap-2">
                      <button
                          type="button"
                          onClick={handleCancelarCondicion}
                          className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md"
                      >
                        Cancelar
                      </button>
                      <button
                          type="button"
                          onClick={handleAgregarCondicion}
                          className="bg-[#B04708] hover:bg-[#8a3606] text-white py-2 px-4 rounded-md"
                      >
                        Agregar condición
                      </button>
                    </div>
                  </div>
                </div>
            )}

            {inverseError && (
              <div className="px-6 pb-2 text-xs text-red-600">{inverseError}</div>
            )}
            {formularioInvalido && (
              <div className="px-6 pb-2 text-xs text-red-600">
                {erroresFormulario[0]}
              </div>
            )}
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                  onClick={handleLimpiar}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded-md transition-all duration-150 hover:scale-105 disabled:opacity-50"
                  disabled={loading || submitPreparing || confirmInversaOpen || confirmGuardarOpen}
              >
                Limpiar
              </button>

              {modoEdicion && (
                  <button
                      onClick={handleAbrirModalInhabilitar}
                      className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-6 rounded-md transition-all duration-150 hover:scale-105 disabled:opacity-50"
                      disabled={loading || submitPreparing || inhabilitarLoading || confirmInversaOpen || confirmGuardarOpen}
                  >
                    {loading || submitPreparing || inhabilitarLoading || confirmInversaOpen || confirmGuardarOpen ? "Procesando..." : "Inhabilitar"}
                  </button>
              )}

              <button
                  onClick={handleSubmitCrearReglaConFeedback}
                  className="bg-[#B04708] hover:bg-[#8a3606] text-white font-medium py-2 px-6 rounded-md transition-all duration-150 hover:scale-105 disabled:opacity-50 inline-flex items-center gap-2"
                  disabled={loading || submitPreparing || formularioInvalido || confirmInversaOpen || confirmGuardarOpen}
              >
                {(submitPreparing || confirmInversaOpen || confirmGuardarOpen) && !loading && (
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                )}
                {loading ? "Guardando..." : (submitPreparing || confirmInversaOpen || confirmGuardarOpen) ? "Preparando..." : modoEdicion ? "Actualizar" : "Crear"}
              </button>
            </div>
          </div>
        </div>
        {modalPracticaAbierto && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-md mx-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Práctica fuera de PMO</h3>
              </div>
              <div className="px-6 py-4 flex flex-col gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Código</label>
                  <input
                    type="text"
                    value={practicaNuevaCodigo || "No se pudo reservar un código"}
                    readOnly
                    className="font-sans py-2 px-4 rounded-md border border-gray-300 bg-gray-100 cursor-not-allowed"
                  />
                  <span className="text-xs text-gray-500 mt-1">
                    {practicaNuevaCodigo
                      ? "Lo asigna el sistema y ya quedó reservado para esta práctica."
                      : "Cerrá y volvé a intentar en unos minutos."}
                  </span>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Descripción</label>
                  <input
                    type="text"
                    value={practicaNuevaDescripcion}
                    onChange={(e) => setPracticaNuevaDescripcion(e.target.value)}
                    placeholder="Ingrese la descripción de la práctica"
                    autoFocus
                    className="font-sans py-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B04708] focus:border-transparent transition duration-150"
                  />
                </div>
                {practicaNuevaError && (
                  <span className="text-xs text-red-600">{practicaNuevaError}</span>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalPracticaAbierto(false)}
                  disabled={practicaNuevaGuardando}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white disabled:opacity-50"
                >
                  Volver
                </button>
                <button
                  type="button"
                  onClick={confirmarPracticaNueva}
                  disabled={practicaNuevaGuardando || !practicaNuevaCodigo}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-[#B04708] disabled:opacity-50"
                >
                  {practicaNuevaGuardando ? "Guardando..." : "Aceptar"}
                </button>
              </div>
            </div>
          </div>
        )}
        {confirmGuardarOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-md mx-4 overflow-hidden">
              {!loading && !mensaje && !confirmGuardarLoading && (
                <>
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {modoEdicion ? "Confirmar actualización" : "Confirmar creación"}
                    </h3>
                  </div>
                  <div className="px-6 py-4 text-sm text-gray-700">
                    Se guardarán los datos actuales
                    {usarInversaPendiente ? " y se generará también la regla inversa." : "."}
                  </div>
                  {modoEdicion && guardarRelacion?.parejaRule && (
                    <div className="px-6 pb-2 text-sm text-gray-700">
                      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <div><span className="font-semibold">Regla actual:</span> {guardarRelacion.currentRuleId}</div>
                        <div><span className="font-semibold">Regla relacionada:</span> {guardarRelacion.parejaRuleId}</div>
                        <div>
                          <span className="font-semibold">Acción pareja actual:</span>{" "}
                          {resolverDecisionRegla(guardarRelacion?.parejaRule) || "-"}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setActualizarConPareja(false)}
                          className={`px-3 py-1.5 rounded-md border text-sm font-medium ${
                            !actualizarConPareja
                              ? "bg-red-50 border-red-200 text-red-700"
                              : "bg-white border-gray-300 text-gray-700"
                          }`}
                        >
                          Solo esta regla
                        </button>
                        <button
                          type="button"
                          onClick={() => setActualizarConPareja(true)}
                          className={`px-3 py-1.5 rounded-md border text-sm font-medium ${
                            actualizarConPareja
                              ? "bg-[#fff3eb] border-[#e7b08e] text-[#8a3606]"
                              : "bg-white border-gray-300 text-gray-700"
                          }`}
                          disabled={!guardarRelacion?.parejaActiva}
                        >
                          Actualizar ambas
                        </button>
                      </div>
                      {actualizarConPareja && guardarRelacion?.parejaActiva && (
                        <div className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-3">
                          <div className="text-xs font-semibold text-gray-600 mb-2">Acción para la regla relacionada</div>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <button
                              type="button"
                              onClick={() => {
                                setModoAccionPareja("mantener");
                                setAccionParejaError("");
                              }}
                              className={`px-3 py-1.5 rounded-md border text-xs font-medium ${
                                modoAccionPareja === "mantener"
                                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                  : "bg-white border-gray-300 text-gray-700"
                              }`}
                            >
                              Mantener acción actual
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setModoAccionPareja("elegir");
                                setAccionParejaError("");
                              }}
                              className={`px-3 py-1.5 rounded-md border text-xs font-medium ${
                                modoAccionPareja === "elegir"
                                  ? "bg-[#fff3eb] border-[#e7b08e] text-[#8a3606]"
                                  : "bg-white border-gray-300 text-gray-700"
                              }`}
                            >
                              Elegir acción
                            </button>
                          </div>
                          {modoAccionPareja === "elegir" && (
                            <Select
                              options={opcionesAccionPareja}
                              value={accionParejaSeleccionada}
                              onChange={(selected) => {
                                setAccionParejaSeleccionada(selected || null);
                                setAccionParejaError("");
                              }}
                              placeholder="Seleccionar acción para la pareja..."
                              styles={customStyles}
                              isClearable
                            />
                          )}
                          {modoAccionPareja === "elegir" && accionParejaError && (
                            <p className="mt-2 text-xs text-red-600">{accionParejaError}</p>
                          )}
                        </div>
                      )}
                      {!guardarRelacion?.parejaActiva && (
                        <p className="mt-2 text-xs text-amber-700">
                          La regla relacionada está deshabilitada, se actualizará solo la actual.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={cerrarConfirmGuardar}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md"
                      disabled={loading || confirmGuardarLoading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmarGuardar}
                      className="bg-[#B04708] hover:bg-[#8a3606] text-white py-2 px-4 rounded-md"
                      disabled={loading || confirmGuardarLoading}
                    >
                      Confirmar
                    </button>
                  </div>
                </>
              )}

              {confirmGuardarLoading && !loading && !mensaje && (
                <div className="px-6 py-10 flex flex-col items-center justify-center">
                  <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#B04708] animate-spin" />
                  <p className="mt-4 text-sm font-medium text-slate-700">
                    Validando regla...
                  </p>
                  <p className="mt-1 text-xs text-slate-500 text-center">
                    Estamos verificando si existe una inversa y si hay duplicados exactos.
                  </p>
                </div>
              )}

              {loading && (
                <div className="px-6 py-10 flex flex-col items-center justify-center">
                  <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#B04708] animate-spin" />
                  <p className="mt-4 text-sm font-medium text-slate-700">
                    {modoEdicion ? "Actualizando regla..." : "Creando regla..."}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Estamos validando y guardando la información.</p>
                  <div className="mt-4 w-full max-w-xs">
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#B04708] transition-all duration-500"
                        style={{ width: `${loadingProgress}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-slate-500 text-center">{loadingProgress}%</p>
                  </div>
                </div>
              )}

              {!loading && mensaje && (
                <>
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: resultadoAcentoOscuro }}>
                      <span
                        className="h-7 w-7 rounded-full border flex items-center justify-center text-sm font-bold"
                        style={{ borderColor: decisionTone.border, backgroundColor: decisionTone.soft, color: resultadoAcentoOscuro }}
                      >
                        {resultadoModalIcono}
                      </span>
                      {resultadoModalTextos.titulo}
                    </h3>
                  </div>
                  <div className="px-6 py-4 text-sm text-slate-700">
                    <p>{resultadoModalTextos.subtitulo}</p>
                    {detalleCortoResultado && (
                      <div
                        className="mt-3 rounded-lg border px-3 py-2"
                        style={{ borderColor: decisionTone.border, backgroundColor: decisionTone.surfaceEnd }}
                      >
                        <p className="text-sm">{detalleCortoResultado}</p>
                      </div>
                    )}
                  </div>
                  <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleMensajeModificarRegla}
                      className="bg-white border text-slate-700 py-2 px-4 rounded-md font-medium"
                      style={{ borderColor: "#cbd5e1" }}
                    >
                      Modificar esta regla
                    </button>
                    <button
                      type="button"
                      onClick={handleMensajeVolverLista}
                      className="text-white py-2 px-4 rounded-md font-medium"
                      style={{ backgroundColor: resultadoAcento }}
                    >
                      Volver a la lista
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {confirmInversaOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div
              className={`bg-white rounded-2xl shadow-lg w-full mx-4 ${modoEdicion ? "max-w-xl" : "max-w-md"}`}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">
                  {modoEdicion ? "Confirmar actualizacion con inversa" : "Confirmar regla inversa"}
                </h3>
              </div>
              <div className="px-6 py-4 text-sm text-gray-700">
                {modoEdicion
                  ? "Se va a actualizar la regla base. ¿Queres generar tambien la regla inversa automatica?"
                  : "Se va a crear la regla base. ¿Queres generar tambien la regla inversa automatica?"}
              </div>
              <div className="px-6 pb-2">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Acción de la regla inversa</label>
                <Select
                  options={opcionesAccionInversa}
                  value={accionInversaSeleccionada}
                  onChange={(selected) => {
                    setAccionInversaSeleccionada(selected || null);
                    if (selected) setInverseError(null);
                  }}
                  placeholder="Seleccionar acción inversa..."
                  styles={customStyles}
                  isClearable
                />
                {inverseError && (
                  <div className="pt-2 text-xs text-red-600">{inverseError}</div>
                )}
              </div>
              <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmInversaOpen(false);
                    setAccionInversaSeleccionada(null);
                    setInverseError(null);
                  }}
                  className={`bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md ${
                    modoEdicion ? "min-w-[130px] whitespace-nowrap text-sm sm:text-base" : ""
                  }`}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirmInversa(false)}
                  className={`bg-white border border-[#B04708] text-[#B04708] hover:bg-[#fff2f0] py-2 px-4 rounded-md ${
                    modoEdicion ? "min-w-[150px] whitespace-nowrap text-sm sm:text-base" : ""
                  }`}
                  disabled={loading}
                >
                  {modoEdicion ? "Solo actualizar" : "Solo base"}
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirmInversa(true, accionInversaSeleccionada?.value)}
                  className={`bg-[#B04708] hover:bg-[#8a3606] text-white py-2 px-4 rounded-md ${
                    modoEdicion ? "min-w-[180px] whitespace-nowrap text-sm sm:text-base" : ""
                  }`}
                  disabled={loading}
                >
                  {modoEdicion ? "Actualizar + inversa" : "Base + inversa"}
                </button>
              </div>
            </div>
          </div>
        )}
        {confirmInhabilitarOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-md mx-4">
              {!loading && !inhabilitarLoading && !inhabilitarResultado && (
                <>
                  {pasoInhabilitar === "confirm" && (
                    <>
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800">Confirmar inhabilitación</h3>
                      </div>
                      <div className="px-6 py-4 text-sm text-gray-700">
                        ¿Seguro que querés inhabilitar esta regla? La regla dejará de aplicarse.
                      </div>
                    </>
                  )}
                  {pasoInhabilitar === "relation" && inhabilitarRelacion?.parejaRule && (
                    <>
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800">Regla relacionada detectada</h3>
                      </div>
                      <div className="px-6 py-4 text-sm text-gray-700 space-y-2">
                        <p>
                          Esta regla tiene una pareja {inhabilitarRelacion.esActualInversa ? "base" : "inversa"}:
                        </p>
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                          <div><span className="font-semibold">Actual:</span> {inhabilitarRelacion.currentRuleId}</div>
                          <div><span className="font-semibold">Relacionada:</span> {inhabilitarRelacion.parejaRuleId}</div>
                        </div>
                        {!inhabilitarRelacion.parejaActiva && (
                          <p className="text-amber-700">
                            La regla relacionada ya está deshabilitada. Solo se inhabilitará la actual.
                          </p>
                        )}
                        {inhabilitarRelacion.parejaActiva && (
                          <div className="pt-2">
                            <div className="text-xs font-semibold text-gray-600 mb-2">Alcance de la inhabilitación</div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setModoInhabilitarRelacion("solo")}
                                className={`px-3 py-1.5 rounded-md border text-xs font-medium ${
                                  modoInhabilitarRelacion === "solo"
                                    ? "bg-red-50 border-red-200 text-red-700"
                                    : "bg-white border-gray-300 text-gray-700"
                                }`}
                              >
                                Solo esta regla
                              </button>
                              <button
                                type="button"
                                onClick={() => setModoInhabilitarRelacion("ambas")}
                                className={`px-3 py-1.5 rounded-md border text-xs font-medium ${
                                  modoInhabilitarRelacion === "ambas"
                                    ? "bg-[#fff3eb] border-[#e7b08e] text-[#8a3606]"
                                    : "bg-white border-gray-300 text-gray-700"
                                }`}
                              >
                                Inhabilitar ambas
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {inhabilitarError && (
                    <div className="px-6 pb-2 text-sm text-red-600">{inhabilitarError}</div>
                  )}
                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={cerrarModalInhabilitar}
                      className="h-11 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 rounded-md font-medium whitespace-nowrap"
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    {pasoInhabilitar === "confirm" ? (
                      <button
                        type="button"
                        onClick={handlePrepararInhabilitacion}
                        className="h-11 bg-red-500 hover:bg-red-600 text-white px-4 rounded-md font-medium whitespace-nowrap"
                        disabled={loading}
                      >
                        Inhabilitar
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setPasoInhabilitar("confirm")}
                          className="h-11 bg-white border border-slate-300 text-slate-700 px-4 rounded-md font-medium whitespace-nowrap"
                          disabled={loading}
                        >
                          Volver
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            ejecutarInhabilitacion({
                              incluirPareja: modoInhabilitarRelacion === "ambas",
                            })
                          }
                          className="h-11 bg-[#B04708] hover:bg-[#8a3606] text-white px-4 rounded-md font-medium whitespace-nowrap"
                          disabled={loading}
                        >
                          Confirmar inhabilitación
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}

              {inhabilitarLoading && !loading && pasoInhabilitar === "loading" && (
                <div className="px-6 py-10 flex flex-col items-center justify-center">
                  <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-blue-500 animate-spin" />
                  <p className="mt-4 text-sm font-medium text-slate-700">Buscando reglas relacionadas...</p>
                  <p className="mt-1 text-xs text-slate-500">Estamos verificando si existen reglas vinculadas.</p>
                </div>
              )}

              {loading && (
                <div className="px-6 py-10 flex flex-col items-center justify-center">
                  <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-red-500 animate-spin" />
                  <p className="mt-4 text-sm font-medium text-slate-700">Inhabilitando regla...</p>
                  <p className="mt-1 text-xs text-slate-500">Estamos procesando la solicitud.</p>
                  <div className="mt-4 w-full max-w-xs">
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-500 transition-all duration-500"
                        style={{ width: `${inhabilitarProgress}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-slate-500 text-center">{inhabilitarProgress}%</p>
                  </div>
                </div>
              )}

              {!loading && inhabilitarResultado && (
                <>
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3
                      className="text-lg font-semibold flex items-center gap-2"
                      style={{ color: inhabilitarResultado.ok ? "#166534" : "#7f1d1d" }}
                    >
                      <span
                        className="h-7 w-7 rounded-full border flex items-center justify-center text-sm font-bold"
                        style={{
                          borderColor: inhabilitarResultado.ok ? "#a7f3d0" : "#fecaca",
                          backgroundColor: inhabilitarResultado.ok ? "#dcfce7" : "#fee2e2",
                          color: inhabilitarResultado.ok ? "#166534" : "#7f1d1d",
                        }}
                      >
                        {inhabilitarResultado.ok ? "✓" : "✕"}
                      </span>
                      {inhabilitarResultado.title}
                    </h3>
                  </div>
                  <div className="px-6 py-4 text-sm text-gray-700">{inhabilitarResultado.detail}</div>
                  <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                    {!inhabilitarResultado.ok && (
                      <button
                        type="button"
                        onClick={cerrarModalInhabilitar}
                        className="bg-white border border-slate-300 text-slate-700 py-2 px-4 rounded-md font-medium"
                      >
                        Seguir editando
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        cerrarModalInhabilitar();
                        navigate("/rulesView");
                      }}
                      className="bg-[#B04708] hover:bg-[#8a3606] text-white py-2 px-4 rounded-md font-medium"
                    >
                      Volver a la lista
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
  );
}

export default Reglas;













